import React, { useState, useEffect, useCallback, useRef } from 'react';
import io from 'socket.io-client';
import API from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import toast from 'react-hot-toast';
import {
    AreaChart, Area, BarChart, Bar, XAxis, YAxis,
    CartesianGrid, Tooltip, ResponsiveContainer, LineChart,
    Line, Legend, PieChart, Pie, Cell
} from 'recharts';
import {
    FiUsers, FiFileText, FiTrash2,
    FiRefreshCw, FiSearch, FiTrendingUp, FiTrendingDown,
    FiCalendar, FiEye, FiEyeOff, FiChevronLeft, FiChevronRight,
    FiAlertCircle, FiCheckCircle, FiDollarSign, FiTag,
    FiEdit2, FiPlus, FiX, FiSave, FiLock
} from 'react-icons/fi';
import { calculateAll } from '../utils/numerologyEngine'; // on-the-fly report enrichment

// ── Admin: Change User Password Section ────────────────────────────────────────
function AdminChangePwSection({ userId, userName, T }) {
    const [open, setOpen] = useState(false);
    const [newPwd, setNewPwd] = useState('');
    const [confirmPwd, setConfirmPwd] = useState('');
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');

    const reset = () => { setNewPwd(''); setConfirmPwd(''); setErr(''); setShowPwd(false); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErr('');
        if (newPwd.length < 6) return setErr('Password must be at least 6 characters');
        if (newPwd !== confirmPwd) return setErr('Passwords do not match');
        setLoading(true);
        try {
            const res = await API.put(`/admin/users/${userId}/change-password`, { newPassword: newPwd });
            toast.success(res.data.message || 'Password updated!');
            setOpen(false);
            reset();
        } catch (error) {
            setErr(error.response?.data?.message || 'Failed to update password');
        } finally {
            setLoading(false);
        }
    };

    const inputSt = {
        width: '100%', padding: '10px 40px 10px 14px', borderRadius: 8, fontSize: 13,
        border: `1.5px solid ${T.border}`, background: T.bg, color: T.text,
        outline: 'none', boxSizing: 'border-box',
    };

    return (
        <div style={{ margin: '0 24px 16px' }}>
            {!open ? (
                <button
                    onClick={() => setOpen(true)}
                    style={{ width: '100%', padding: '10px', borderRadius: 10, border: `1.5px dashed ${T.border}`, background: 'transparent', color: T.textSub, fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}
                >
                    <FiLock size={13} /> Change Password for {userName}
                </button>
            ) : (
                <div style={{ background: T.surface, borderRadius: 12, border: `1.5px solid ${T.border}`, padding: '16px 18px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: T.text, display: 'flex', alignItems: 'center', gap: 6 }}><FiLock size={13} style={{ color: '#cead80' }} /> Set New Password</span>
                        <button onClick={() => { setOpen(false); reset(); }} style={{ background: 'none', border: 'none', color: T.textMute, cursor: 'pointer', fontSize: 16, padding: 0 }}><FiX /></button>
                    </div>
                    {err && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '8px 12px', marginBottom: 10, color: '#f87171', fontSize: 12 }}>⚠️ {err}</div>}
                    <form onSubmit={handleSubmit} noValidate>
                        {/* New password */}
                        <div style={{ marginBottom: 10 }}>
                            <label style={{ fontSize: 11, fontWeight: 700, color: T.textMute, display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.5 }}>New Password</label>
                            <div style={{ position: 'relative' }}>
                                <input type={showPwd ? 'text' : 'password'} style={inputSt} placeholder="Min 6 characters" value={newPwd} onChange={e => { setNewPwd(e.target.value); setErr(''); }} autoComplete="new-password" />
                                <button type="button" onClick={() => setShowPwd(s => !s)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: T.textMute, cursor: 'pointer', fontSize: 14, padding: 0 }}>
                                    {showPwd ? <FiEyeOff /> : <FiEye />}
                                </button>
                            </div>
                        </div>
                        {/* Confirm */}
                        <div style={{ marginBottom: 14 }}>
                            <label style={{ fontSize: 11, fontWeight: 700, color: T.textMute, display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.5 }}>Confirm Password</label>
                            <input type="password" style={{ ...inputSt, borderColor: confirmPwd && confirmPwd !== newPwd ? 'rgba(239,68,68,0.5)' : confirmPwd && confirmPwd === newPwd ? 'rgba(34,197,94,0.5)' : T.border }} placeholder="Repeat password" value={confirmPwd} onChange={e => { setConfirmPwd(e.target.value); setErr(''); }} autoComplete="new-password" />
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button type="button" onClick={() => { setOpen(false); reset(); }} style={{ flex: 1, padding: '9px', borderRadius: 8, border: `1.5px solid ${T.border}`, background: 'transparent', color: T.textSub, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                            <button type="submit" disabled={loading} style={{ flex: 2, padding: '9px', borderRadius: 8, border: 'none', background: loading ? 'rgba(206,173,128,0.4)' : 'linear-gradient(135deg,#cead80,#b8944a)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: loading ? 'not-allowed' : 'pointer' }}>
                                {loading ? 'Updating...' : '🔒 Update Password'}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

let socket = null;
function getSocket() {
    if (!socket) socket = io('http://localhost:8000', { transports: ['websocket'] });
    return socket;
}

// ── Theme token factory (dark + light) ────────────────────────────────────────
function makeTokens(isDark) {
    return isDark ? {
        bg: '#080e1a',
        surface: '#111d33',
        surface2: '#162241',
        border: 'rgba(255,255,255,0.07)',
        borderHov: 'rgba(79,110,247,0.4)',
        text: '#eef2ff',
        textSub: '#94a3b8',
        textMute: '#475569',
        indigo: '#4f6ef7',
        indigoLt: 'rgba(79,110,247,0.15)',
        gold: '#f59e0b',
        goldLt: 'rgba(245,158,11,0.15)',
        teal: '#0ea5e9',
        tealLt: 'rgba(14,165,233,0.15)',
        emerald: '#10b981',
        emeraldLt: 'rgba(16,185,129,0.12)',
        rose: '#f43f5e',
        roseLt: 'rgba(244,63,94,0.12)',
        orange: '#f97316',
        orangeLt: 'rgba(249,115,22,0.13)',
        pink: '#ec4899',
        pinkLt: 'rgba(236,72,153,0.12)',
    } : {
        bg: '#f4f6fb',
        surface: '#ffffff',
        surface2: '#f8faff',
        border: '#e8edf5',
        borderHov: '#c7d2fe',
        text: '#1e293b',
        textSub: '#475569',
        textMute: '#94a3b8',
        indigo: '#4f6ef7',
        indigoLt: '#eef2ff',
        gold: '#f59e0b',
        goldLt: '#fffbeb',
        teal: '#0ea5e9',
        tealLt: '#f0f9ff',
        emerald: '#10b981',
        emeraldLt: '#ecfdf5',
        rose: '#f43f5e',
        roseLt: '#fff1f2',
        orange: '#f97316',
        orangeLt: '#fff7ed',
        pink: '#ec4899',
        pinkLt: '#fdf4ff',
    };
}

// ── Custom Tooltip ──────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '12px 18px', boxShadow: '0 8px 28px rgba(79,110,247,0.12)' }}>
            <p style={{ color: '#94a3b8', fontSize: 11, marginBottom: 8, fontWeight: 700, textTransform: 'uppercase' }}>{label}</p>
            {payload.map(p => (
                <p key={p.name} style={{ color: p.color, fontSize: 14, fontWeight: 700 }}>
                    {p.name}: <span style={{ color: '#1e293b' }}>{p.value}</span>
                </p>
            ))}
        </div>
    );
};

// ── Stat Card ──────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, colour, colourLt, sub, trend, animated, prefix }) {
    const { isDark } = useTheme();
    const T = makeTokens(isDark);
    const [display, setDisplay] = useState(0);
    useEffect(() => {
        if (!animated || value == null) return;
        let start = 0; const end = Number(value);
        if (end === 0) { setDisplay(0); return; }
        const step = Math.ceil(end / 40);
        const timer = setInterval(() => { start = Math.min(start + step, end); setDisplay(start); if (start >= end) clearInterval(timer); }, 30);
        return () => clearInterval(timer);
    }, [value, animated]);

    return (
        <div style={{
            background: T.surface, border: `1px solid ${T.border}`,
            borderRadius: 16, padding: '22px 20px',
            position: 'relative', overflow: 'hidden',
            boxShadow: '0 2px 16px rgba(79,110,247,0.07)',
            transition: 'all 0.25s ease',
        }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = T.borderHov; e.currentTarget.style.boxShadow = '0 6px 28px rgba(79,110,247,0.14)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = '0 2px 16px rgba(79,110,247,0.07)'; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
            <div style={{ position: 'absolute', top: -28, right: -28, width: 90, height: 90, borderRadius: '50%', background: colour, opacity: 0.07, filter: 'blur(18px)' }} />
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: colourLt, border: `1.5px solid ${colour}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                    {icon}
                </div>
                {trend != null && (
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700,
                        color: trend >= 0 ? '#16a34a' : '#dc2626',
                        background: trend >= 0 ? '#dcfce7' : '#fee2e2',
                        padding: '4px 10px', borderRadius: 50,
                    }}>
                        {trend >= 0 ? <FiTrendingUp size={11} /> : <FiTrendingDown size={11} />}
                        {Math.abs(trend)}%
                    </div>
                )}
            </div>
            <div style={{ fontSize: 34, fontWeight: 900, color: colour, lineHeight: 1, marginBottom: 5, fontVariantNumeric: 'tabular-nums' }}>
                {prefix}{animated ? display.toLocaleString('en-IN') : (value ?? '—')}
            </div>
            <div style={{ fontSize: 13, color: T.textSub, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.7 }}>{label}</div>
            {sub && <div style={{ fontSize: 11, color: T.textMute, marginTop: 3 }}>{sub}</div>}
        </div>
    );
}

// ── Live Pulse ─────────────────────────────────────────────────────────────────
function LivePulse({ count, connected }) {
    const { isDark } = useTheme();
    const T = makeTokens(isDark);
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: connected ? T.emeraldLt : '#f8fafc',
            border: `1px solid ${connected ? '#a7f3d0' : T.border}`,
            padding: '9px 18px', borderRadius: 50
        }}>
            <div style={{ position: 'relative', width: 10, height: 10 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: connected ? T.emerald : T.textMute }} />
                {connected && <div style={{ position: 'absolute', inset: -4, borderRadius: '50%', border: '2px solid #6ee7b7', animation: 'liveRing 1.5s ease-out infinite' }} />}
            </div>
            <span style={{ color: connected ? '#065f46' : T.textMute, fontWeight: 700, fontSize: 14 }}>
                {count} Live {connected ? 'Online' : '(offline)'}
            </span>
            <FiEye size={14} style={{ color: connected ? T.emerald : T.textMute }} />
        </div>
    );
}

// ── Sidebar ────────────────────────────────────────────────────────────────────
function SideNav({ tab, setTab, stats, liveCount }) {
    const { isDark } = useTheme();
    const T = makeTokens(isDark);
    const items = [
        { key: 'dashboard', label: 'Dashboard', badge: null },
        { key: 'analytics', label: 'Analytics', badge: null },
        { key: 'users', label: 'Users', badge: stats?.totalUsers },
        { key: 'reports', label: 'Reports', badge: stats?.totalReports },
        { key: 'pricing', label: 'Pricing', badge: null },
        { key: 'coupons', label: 'Coupons', badge: null },
        { key: 'bracelets', label: 'Crystals', badge: null },
        { key: 'products', label: 'Other Products', badge: null },
        { key: 'wallpapers', label: 'Wallpapers', badge: null },
        { key: 'mobile-covers', label: 'Mobile Covers', badge: null },
        { key: 'yantras', label: 'Yantra', badge: null },
        { key: 'orders', label: 'Orders', badge: null },
        { key: 'logs', label: 'Logs', badge: null },
    ];
    return (
        <aside style={{
            width: 220, flexShrink: 0,
            background: T.surface, border: `1px solid ${T.border}`,
            borderRadius: 18, padding: '22px 12px',
            display: 'flex', flexDirection: 'column', gap: 3,
            height: 'fit-content', position: 'sticky', top: 90,
            boxShadow: '0 2px 16px rgba(79,110,247,0.06)',
        }}>
            <p style={{ fontSize: 10, fontWeight: 700, color: T.textMute, textTransform: 'uppercase', letterSpacing: 1, padding: '0 12px', marginBottom: 8 }}>
                Navigation
            </p>
            {items.map(item => (
                <button key={item.key} onClick={() => setTab(item.key)} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '11px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
                    background: tab === item.key ? T.indigoLt : 'transparent',
                    color: tab === item.key ? T.indigo : T.textSub,
                    fontWeight: 600, fontSize: 14, transition: 'all 0.18s ease',
                    borderLeft: tab === item.key ? `3px solid ${T.indigo}` : '3px solid transparent',
                    textAlign: 'left', width: '100%',
                }}
                    onMouseEnter={e => { if (tab !== item.key) { e.currentTarget.style.background = T.bg; e.currentTarget.style.color = T.text; } }}
                    onMouseLeave={e => { if (tab !== item.key) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.textSub; } }}
                >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>

                        {item.label}
                    </span>
                    {item.badge != null && (
                        <span style={{
                            background: tab === item.key ? `${T.indigo}20` : T.bg,
                            color: tab === item.key ? T.indigo : T.textMute,
                            fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 50
                        }}>{item.badge}</span>
                    )}
                </button>
            ))}

            <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 16, marginTop: 14 }}>
                <div style={{ background: T.emeraldLt, borderRadius: 12, padding: '12px 14px' }}>
                    <div style={{ fontSize: 10, color: '#065f46', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 700, marginBottom: 4 }}>Live Visitors</div>
                    <div style={{ fontSize: 30, fontWeight: 900, color: T.emerald }}>{liveCount}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                        <div style={{ width: 7, height: 7, borderRadius: '50%', background: T.emerald, animation: 'liveDot 1.4s ease-in-out infinite' }} />
                        <span style={{ fontSize: 11, color: '#065f46', fontWeight: 600 }}>Right now</span>
                    </div>
                </div>
            </div>
        </aside>
    );
}

// ── Pagination ─────────────────────────────────────────────────────────────────
function Pagination({ page, pages, onPage }) {
    const { isDark } = useTheme();
    const T = makeTokens(isDark);
    if (pages <= 1) return null;
    const range = [];
    for (let i = Math.max(1, page - 2); i <= Math.min(pages, page + 2); i++) range.push(i);
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center', padding: 16, borderTop: `1px solid ${T.border}` }}>
            <button onClick={() => onPage(page - 1)} disabled={page === 1}
                style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: '6px 10px', color: T.textSub, cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1 }}>
                <FiChevronLeft size={14} />
            </button>
            {range.map(p => (
                <button key={p} onClick={() => onPage(p)} style={{
                    minWidth: 34, height: 34, borderRadius: 8, border: 'none',
                    background: p === page ? `linear-gradient(135deg,${T.indigo},#3451d1)` : T.bg,
                    color: p === page ? '#fff' : T.textSub,
                    fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'all 0.18s ease'
                }}>{p}</button>
            ))}
            <button onClick={() => onPage(page + 1)} disabled={page === pages}
                style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: '6px 10px', color: T.textSub, cursor: page === pages ? 'not-allowed' : 'pointer', opacity: page === pages ? 0.4 : 1 }}>
                <FiChevronRight size={14} />
            </button>
            <span style={{ fontSize: 12, color: T.textMute, marginLeft: 8 }}>Page {page} of {pages}</span>
        </div>
    );
}

// ── Card wrapper ───────────────────────────────────────────────────────────────
function LCard({ children, style = {} }) {
    const { isDark } = useTheme();
    const T = makeTokens(isDark);
    return (
        <div style={{
            background: T.surface, border: `1px solid ${T.border}`,
            borderRadius: 16, padding: 24,
            boxShadow: '0 2px 16px rgba(79,110,247,0.06)',
            ...style
        }}>{children}</div>
    );
}

// ── Coupon Modal ───────────────────────────────────────────────────────────────
function CouponModal({ coupon, onClose, onSave, T }) {
    const isEdit = !!coupon?._id;
    const [form, setForm] = useState({
        code: coupon?.code || '',
        type: coupon?.type || 'percent',
        value: coupon?.value ?? '',
        minOrderAmount: coupon?.minOrderAmount ?? 0,
        maxUses: coupon?.maxUses ?? '',
        expiresAt: coupon?.expiresAt ? new Date(coupon.expiresAt).toISOString().split('T')[0] : '',
        isActive: coupon?.isActive !== undefined ? coupon.isActive : true,
    });
    const [saving, setSaving] = useState(false);

    const handleSubmit = async () => {
        if (!form.code || !form.type || form.value === '') {
            toast.error('Code, type and value are required.');
            return;
        }
        setSaving(true);
        try {
            await onSave({ ...form, code: form.code.toUpperCase() });
        } finally {
            setSaving(false);
        }
    };

    const inputStyle = {
        width: '100%', padding: '10px 14px',
        background: T.bg, border: `1.5px solid ${T.border}`,
        borderRadius: 10, color: T.text, fontSize: 14,
        outline: 'none', fontFamily: 'inherit',
        boxSizing: 'border-box',
    };
    const labelStyle = { fontSize: 12, fontWeight: 700, color: T.textMute, textTransform: 'uppercase', letterSpacing: 0.7, display: 'block', marginBottom: 6 };

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }} onClick={e => e.target === e.currentTarget && onClose()}>
            <div style={{
                background: T.surface, borderRadius: 20, padding: 32,
                width: '100%', maxWidth: 480,
                border: `1px solid ${T.border}`,
                boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
                animation: 'fadeUp 0.25s ease',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                    <h3 style={{ fontSize: 18, fontWeight: 800, color: T.text }}>
                        {isEdit ? '✏️ Edit Coupon' : '🎟 Create Coupon'}
                    </h3>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: T.textMute, cursor: 'pointer', fontSize: 20 }}>
                        <FiX />
                    </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Code */}
                    <div>
                        <label style={labelStyle}>Coupon Code</label>
                        <input style={{ ...inputStyle, fontFamily: 'monospace', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}
                            placeholder="e.g. HEAL50"
                            value={form.code}
                            onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                        />
                    </div>

                    {/* Type + Value */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                            <label style={labelStyle}>Type</label>
                            <select style={{ ...inputStyle, cursor: 'pointer' }}
                                value={form.type}
                                onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                                <option value="percent">% Percentage</option>
                                <option value="flat">₹ Flat Amount</option>
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>Value ({form.type === 'percent' ? '%' : '₹'})</label>
                            <input style={inputStyle} type="number" min="0"
                                placeholder={form.type === 'percent' ? 'e.g. 20' : 'e.g. 300'}
                                value={form.value}
                                onChange={e => setForm(f => ({ ...f, value: e.target.value }))}
                            />
                        </div>
                    </div>

                    {/* Min Order + Max Uses */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                            <label style={labelStyle}>Min. Order (₹)</label>
                            <input style={inputStyle} type="number" min="0"
                                placeholder="0 = no minimum"
                                value={form.minOrderAmount}
                                onChange={e => setForm(f => ({ ...f, minOrderAmount: e.target.value }))}
                            />
                        </div>
                        <div>
                            <label style={labelStyle}>Max Uses</label>
                            <input style={inputStyle} type="number" min="1"
                                placeholder="Leave blank = unlimited"
                                value={form.maxUses}
                                onChange={e => setForm(f => ({ ...f, maxUses: e.target.value }))}
                            />
                        </div>
                    </div>

                    {/* Expiry */}
                    <div>
                        <label style={labelStyle}>Expiry Date (optional)</label>
                        <input style={inputStyle} type="date"
                            value={form.expiresAt}
                            onChange={e => setForm(f => ({ ...f, expiresAt: e.target.value }))}
                        />
                    </div>

                    {/* Active toggle */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <button
                            onClick={() => setForm(f => ({ ...f, isActive: !f.isActive }))}
                            style={{
                                width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
                                background: form.isActive ? '#10b981' : T.border,
                                position: 'relative', transition: 'background 0.2s',
                                flexShrink: 0,
                            }}>
                            <div style={{
                                position: 'absolute', top: 3, left: form.isActive ? 22 : 3,
                                width: 18, height: 18, borderRadius: '50%', background: '#fff',
                                transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                            }} />
                        </button>
                        <span style={{ fontSize: 14, color: T.textSub, fontWeight: 600 }}>
                            {form.isActive ? '✅ Active' : '⛔ Inactive'}
                        </span>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                        <button onClick={onClose} style={{
                            flex: 1, padding: '12px', borderRadius: 10,
                            border: `1.5px solid ${T.border}`, background: 'transparent',
                            color: T.textSub, fontWeight: 600, fontSize: 14, cursor: 'pointer',
                        }}>Cancel</button>
                        <button onClick={handleSubmit} disabled={saving} style={{
                            flex: 2, padding: '12px', borderRadius: 10, border: 'none',
                            background: 'linear-gradient(135deg, #4f6ef7, #3451d1)',
                            color: '#fff', fontWeight: 700, fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                            boxShadow: '0 4px 16px rgba(79,110,247,0.3)',
                        }}>
                            <FiSave size={15} /> {saving ? 'Saving…' : (isEdit ? 'Update Coupon' : 'Create Coupon')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════════
export default function AdminPanel() {
    const { user } = useAuth();
    const { isDark, toggleTheme } = useTheme();
    const T = makeTokens(isDark);
    const [tab, setTab] = useState('dashboard');
    const [stats, setStats] = useState(null);
    const [weekly, setWeekly] = useState([]);
    const [monthly, setMonthly] = useState([]);
    const [analyticsView, setAnalyticsView] = useState('weekly');
    const [liveCount, setLiveCount] = useState(0);
    const [socketConnected, setSocketConnected] = useState(false);
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [usersTotal, setUsersTotal] = useState(0);
    const [usersPage, setUsersPage] = useState(1);
    const [usersPages, setUsersPages] = useState(1);
    const [usersSearch, setUsersSearch] = useState('');
    const [usersFilter, setUsersFilter] = useState('all');   // 'all' | 'paid' | 'free'
    const [usersSort, setUsersSort] = useState('newest'); // 'newest' | 'oldest' | 'reports'
    const [usersLoading, setUsersLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);  // for detail panel
    const [userDetailReports, setUserDetailReports] = useState([]);
    const [userDetailLoading, setUserDetailLoading] = useState(false);
    const [showAddUserModal, setShowAddUserModal] = useState(false);
    const [addUserSaving, setAddUserSaving] = useState(false);
    const [addUserForm, setAddUserForm] = useState({ firstName: '', lastName: '', email: '', phone: '', password: '', grantSubscription: false });
    // ── Email Invitation state ────────────────────────────────────────────────
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteForm, setInviteForm] = useState({ email: '', grantSubscription: true });
    const [inviteSending, setInviteSending] = useState(false);
    const [reports, setReports] = useState([]);
    const [reportsTotal, setReportsTotal] = useState(0);
    const [reportsPage, setReportsPage] = useState(1);
    const [reportsPages, setReportsPages] = useState(1);
    const [reportsLoading, setReportsLoading] = useState(false);
    const [expandedReport, setExpandedReport] = useState(null);
    const [reportsSubTab, setReportsSubTab] = useState('all'); // 'all' | 'duplicates' | 'delete-by-date'
    const [deleteStartDate, setDeleteStartDate] = useState('');
    const [deleteEndDate, setDeleteEndDate] = useState('');
    const [deletingByDate, setDeletingByDate] = useState(false);
    const [duplicates, setDuplicates] = useState([]);
    const [duplicatesLoading, setDuplicatesLoading] = useState(false);
    const [deletingDuplicates, setDeletingDuplicates] = useState(false);
    const [deletingDupReport, setDeletingDupReport] = useState(null);
    const searchRef = useRef(null);

    // ── Pricing state ──────────────────────────────────────────────────────────
    const [pricingForm, setPricingForm] = useState({
        toolName: 'Loshu Grid Calculator',
        toolPrice: 1500,
        toolOriginalPrice: 3000,
        offerLabel: '50% OFF — Limited Time',
        seleniteActive: true,
        seleniteName: 'Crystal Charging Selenite Plate',
        selenitePrice: 959,
        seleniteOriginalPrice: 1599,
        seleniteImageUrl: '',
        seleniteLink: '',
    });
    const [pricingLoading, setPricingLoading] = useState(false);
    const [pricingSaving, setPricingSaving] = useState(false);

    // ── Coupons state ──────────────────────────────────────────────────────────
    const [coupons, setCoupons] = useState([]);
    const [couponsLoading, setCouponsLoading] = useState(false);
    const [couponModal, setCouponModal] = useState(null); // null | {} | {coupon object}
    const [deletingCoupon, setDeletingCoupon] = useState(null);

    // ── Bracelets state ────────────────────────────────────────────────────────
    const [bracelets, setBracelets] = useState([]);
    const [braceletsLoading, setBraceletsLoading] = useState(false);
    const [braceletModal, setBraceletModal] = useState(null);
    const [deletingBracelet, setDeletingBracelet] = useState(null);
    const [deletingReport, setDeletingReport] = useState(null);
    const BRACELET_COLORS = { 1: '#F59E0B', 2: '#38BDF8', 3: '#A78BFA', 4: '#6EE7B7', 5: '#34D399', 6: '#F472B6', 7: '#818CF8', 8: '#94A3B8', 9: '#F87171' };

    // ── Common Products state ──────────────────────────────────────────────────
    const [commonProducts, setCommonProducts] = useState([]);
    const [commonProductsLoading, setCommonProductsLoading] = useState(false);
    const [commonProductModal, setCommonProductModal] = useState(null);
    const [deletingCommonProduct, setDeletingCommonProduct] = useState(null);

    // ── Wallpapers state ───────────────────────────────────────────────────────
    const [wallpapers, setWallpapers] = useState([]);  // array of { number, images }
    const [wallpapersLoading, setWallpapersLoading] = useState(false);
    const [wpForm, setWpForm] = useState({ number: 1, url: '', label: '' });
    const [wpSaving, setWpSaving] = useState(false);
    const [wpDeleting, setWpDeleting] = useState(null); // imageId being deleted
    const WP_COLORS = { 1: '#ef4444', 2: '#3b82f6', 3: '#a855f7', 4: '#06b6d4', 5: '#22c55e', 6: '#ec4899', 7: '#6b7280', 8: '#1e293b', 9: '#9f1239' };
    const WP_EMOJI = { 1: '☀️', 2: '🌙', 3: '✨', 4: '🌊', 5: '🌿', 6: '💫', 7: '🔮', 8: '⚡', 9: '🔥' };

    // ── Mobile Covers state ────────────────────────────────────────────────────
    const [mobileCovers, setMobileCovers] = useState([]);
    const [mobileCoversLoading, setMobileCoversLoading] = useState(false);
    const [mcForm, setMcForm] = useState({ number: 1, url: '', label: '' });
    const [mcSaving, setMcSaving] = useState(false);
    const [mcDeleting, setMcDeleting] = useState(null);
    const MC_COLORS = { 1: '#F59E0B', 2: '#38BDF8', 3: '#A78BFA', 4: '#6EE7B7', 5: '#34D399', 6: '#F472B6', 7: '#818CF8', 8: '#94A3B8', 9: '#F87171' };
    const MC_EMOJI = { 1: '☀️', 2: '🌙', 3: '✨', 4: '🌊', 5: '🌿', 6: '💫', 7: '🔮', 8: '⚡', 9: '🔥' };

    // ── Yantras state ──────────────────────────────────────────────────────────
    const [yantras, setYantras] = useState([]);  // array of { number, images }
    const [yantrasLoading, setYantrasLoading] = useState(false);
    const [yantraForm, setYantraForm] = useState({ number: 1, url: '', label: '' });
    const [yantraSaving, setYantraSaving] = useState(false);
    const [yantraDeleting, setYantraDeleting] = useState(null); // imageId being deleted
    const YANTRA_COLORS = { 1: '#ef4444', 2: '#3b82f6', 3: '#a855f7', 4: '#06b6d4', 5: '#22c55e', 6: '#ec4899', 7: '#6b7280', 8: '#1e293b', 9: '#9f1239' };
    const YANTRA_EMOJI = { 1: '🔱', 2: '☽', 3: '🕉️', 4: '🌀', 5: '⚡', 6: '✡️', 7: '🔯', 8: '☸️', 9: '🔺' };

    // ── Orders state ───────────────────────────────────────────────────────────
    const [orders, setOrders] = useState([]);
    const [ordersTotal, setOrdersTotal] = useState(0);
    const [ordersPage, setOrdersPage] = useState(1);
    const [ordersPages, setOrdersPages] = useState(1);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [ordersFilter, setOrdersFilter] = useState('all'); // 'all' | 'paid' | 'shipped' | 'delivered'
    const [ordersRevenue, setOrdersRevenue] = useState(0);
    const [selectedOrder, setSelectedOrder] = useState(null); // stores order details for modal view

    // ── Logs state ────────────────────────────────────────────────────────────
    const [logs, setLogs] = useState([]);
    const [logsTotal, setLogsTotal] = useState(0);
    const [logsPage, setLogsPage] = useState(1);
    const [logsPages, setLogsPages] = useState(1);
    const [logsLoading, setLogsLoading] = useState(false);
    const [logsType, setLogsType] = useState('all');   // 'all' | 'report' | 'user'
    const [logsSearch, setLogsSearch] = useState('');
    const [logsClearLoading, setLogsClearLoading] = useState(false);

    useEffect(() => {
        const s = getSocket();
        const onConnect = () => setSocketConnected(true);
        const onDisconnect = () => setSocketConnected(false);
        const onVisitors = c => setLiveCount(c);
        s.on('connect', onConnect); s.on('disconnect', onDisconnect); s.on('liveVisitors', onVisitors);
        if (s.connected) setSocketConnected(true);
        return () => { s.off('connect', onConnect); s.off('disconnect', onDisconnect); s.off('liveVisitors', onVisitors); };
    }, []);

    const loadDashboard = useCallback(async () => {
        setLoading(true);
        try {
            const [s, w, m] = await Promise.all([API.get('/admin/dashboard'), API.get('/admin/analytics/weekly'), API.get('/admin/analytics/monthly')]);
            setStats(s.data.data); setWeekly(w.data.data); setMonthly(m.data.data);
        } catch { toast.error('Failed to load dashboard data'); } finally { setLoading(false); }
    }, []);

    const loadUsers = useCallback(async () => {
        setUsersLoading(true);
        try {
            const res = await API.get(`/admin/users?page=${usersPage}&limit=10&search=${usersSearch}&filter=${usersFilter}&sort=${usersSort}`);
            setUsers(res.data.data); setUsersTotal(res.data.total); setUsersPages(res.data.pages);
        } catch { toast.error('Failed to load users'); } finally { setUsersLoading(false); }
    }, [usersPage, usersSearch, usersFilter, usersSort]);

    const loadReports = useCallback(async () => {
        setReportsLoading(true);
        try {
            const res = await API.get(`/admin/reports?page=${reportsPage}&limit=10`);
            // Enrich each raw report with computed numerology values
            const enriched = (res.data.data || []).map(r => ({
                ...r,
                ...calculateAll({ dob: r.dob, gender: r.gender }),
            }));
            setReports(enriched); setReportsTotal(res.data.total); setReportsPages(res.data.pages);
        } catch { toast.error('Failed to load reports'); } finally { setReportsLoading(false); }
    }, [reportsPage]);

    const loadPricing = useCallback(async () => {
        setPricingLoading(true);
        try {
            const res = await API.get('/admin/settings');
            if (res.data.success) setPricingForm(res.data.data);
        } catch { toast.error('Failed to load settings'); } finally { setPricingLoading(false); }
    }, []);

    const loadCoupons = useCallback(async () => {
        setCouponsLoading(true);
        try {
            const res = await API.get('/admin/coupons');
            if (res.data.success) setCoupons(res.data.data);
        } catch { toast.error('Failed to load coupons'); } finally { setCouponsLoading(false); }
    }, []);

    const loadBracelets = useCallback(async () => {
        setBraceletsLoading(true);
        try {
            const res = await API.get('/admin/bracelets');
            if (res.data.success) setBracelets(res.data.data);
        } catch { toast.error('Failed to load bracelets'); } finally { setBraceletsLoading(false); }
    }, []);

    const loadCommonProducts = useCallback(async () => {
        setCommonProductsLoading(true);
        try {
            const res = await API.get('/common-products/admin');
            if (res.data.success) setCommonProducts(res.data.data);
        } catch { toast.error('Failed to load products'); } finally { setCommonProductsLoading(false); }
    }, []);

    const loadWallpapers = async () => {
        setWallpapersLoading(true);
        try {
            const res = await API.get('/wallpapers/admin/all');
            if (res.data.success) setWallpapers(res.data.data);
        } catch { toast.error('Failed to load wallpapers'); } finally { setWallpapersLoading(false); }
    };

    const handleAddWallpaperImage = async () => {
        if (!wpForm.url.trim()) return toast.error('Please enter an image URL.');
        setWpSaving(true);
        try {
            await API.post(`/wallpapers/admin/${wpForm.number}/images`, { url: wpForm.url, label: wpForm.label || 'Style' });
            toast.success('Wallpaper image added!');
            setWpForm(f => ({ ...f, url: '', label: '' }));
            loadWallpapers();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed to add wallpaper.'); }
        finally { setWpSaving(false); }
    };

    const handleDeleteWallpaperImage = async (number, imageId) => {
        if (!window.confirm('Delete this wallpaper image?')) return;
        setWpDeleting(imageId);
        try {
            await API.delete(`/wallpapers/admin/${number}/images/${imageId}`);
            toast.success('Image deleted.');
            loadWallpapers();
        } catch { toast.error('Failed to delete.'); } finally { setWpDeleting(null); }
    };

    const handleToggleWallpaper = async (number) => {
        try {
            const res = await API.patch(`/wallpapers/admin/${number}/toggle`);
            toast.success(res.data.message);
            loadWallpapers();
        } catch { toast.error('Failed to toggle.'); }
    };

    useEffect(() => { loadDashboard(); }, [loadDashboard]);
    useEffect(() => { if (tab === 'users') loadUsers(); }, [tab, loadUsers]);
    useEffect(() => { if (tab === 'reports') loadReports(); }, [tab, loadReports]);
    useEffect(() => { if (tab === 'pricing') loadPricing(); }, [tab, loadPricing]);
    useEffect(() => { if (tab === 'coupons') loadCoupons(); }, [tab, loadCoupons]);
    useEffect(() => { if (tab === 'bracelets') loadBracelets(); }, [tab, loadBracelets]);
    useEffect(() => { if (tab === 'products') loadCommonProducts(); }, [tab, loadCommonProducts]);
    useEffect(() => { if (tab === 'wallpapers') loadWallpapers(); }, [tab]); // eslint-disable-line

    const loadMobileCovers = async () => {
        setMobileCoversLoading(true);
        try {
            const res = await API.get('/mobile-covers/admin/all');
            if (res.data.success) setMobileCovers(res.data.data);
        } catch { toast.error('Failed to load mobile covers'); } finally { setMobileCoversLoading(false); }
    };

    const handleAddMobileCoverImage = async () => {
        if (!mcForm.url.trim()) return toast.error('Please enter an image URL.');
        setMcSaving(true);
        try {
            await API.post(`/mobile-covers/admin/${mcForm.number}/images`, { url: mcForm.url, label: mcForm.label || 'Cover' });
            toast.success('Mobile cover image added!');
            setMcForm(f => ({ ...f, url: '', label: '' }));
            loadMobileCovers();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed to add image.'); }
        finally { setMcSaving(false); }
    };

    const handleDeleteMobileCoverImage = async (number, imageId) => {
        if (!window.confirm('Delete this mobile cover image?')) return;
        setMcDeleting(imageId);
        try {
            await API.delete(`/mobile-covers/admin/${number}/images/${imageId}`);
            toast.success('Image deleted.');
            loadMobileCovers();
        } catch { toast.error('Failed to delete.'); } finally { setMcDeleting(null); }
    };

    const handleToggleMobileCover = async (number) => {
        try {
            const res = await API.patch(`/mobile-covers/admin/${number}/toggle`);
            toast.success(res.data.message);
            loadMobileCovers();
        } catch { toast.error('Failed to toggle.'); }
    };

    useEffect(() => { if (tab === 'mobile-covers') loadMobileCovers(); }, [tab]); // eslint-disable-line

    const loadYantras = async () => {
        setYantrasLoading(true);
        try {
            const res = await API.get('/yantras/admin/all');
            if (res.data.success) setYantras(res.data.data);
        } catch { toast.error('Failed to load yantras'); } finally { setYantrasLoading(false); }
    };

    const handleAddYantraImage = async () => {
        if (!yantraForm.url.trim()) return toast.error('Please enter an image URL.');
        setYantraSaving(true);
        try {
            await API.post(`/yantras/admin/${yantraForm.number}/images`, { url: yantraForm.url, label: yantraForm.label || 'Yantra' });
            toast.success('Yantra image added!');
            setYantraForm(f => ({ ...f, url: '', label: '' }));
            loadYantras();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed to add yantra.'); }
        finally { setYantraSaving(false); }
    };

    const handleDeleteYantraImage = async (number, imageId) => {
        if (!window.confirm('Delete this yantra image?')) return;
        setYantraDeleting(imageId);
        try {
            await API.delete(`/yantras/admin/${number}/images/${imageId}`);
            toast.success('Image deleted.');
            loadYantras();
        } catch { toast.error('Failed to delete.'); } finally { setYantraDeleting(null); }
    };

    const handleToggleYantra = async (number) => {
        try {
            const res = await API.patch(`/yantras/admin/${number}/toggle`);
            toast.success(res.data.message);
            loadYantras();
        } catch { toast.error('Failed to toggle.'); }
    };

    useEffect(() => { if (tab === 'yantras') loadYantras(); }, [tab]); // eslint-disable-line

    const loadLogs = useCallback(async () => {
        setLogsLoading(true);
        try {
            const res = await API.get(`/admin/logs?page=${logsPage}&limit=50&type=${logsType}&search=${encodeURIComponent(logsSearch)}`);
            if (res.data.success) {
                setLogs(res.data.data);
                setLogsTotal(res.data.total);
                setLogsPages(res.data.pages);
            }
        } catch { toast.error('Failed to load logs'); }
        finally { setLogsLoading(false); }
    }, [logsPage, logsType, logsSearch]);

    // Clear all logs — stores a timestamp in Settings; the backend filters entries before it
    const handleClearLogs = async () => {
        if (!window.confirm('Clear all logs? This cannot be undone.')) return;
        setLogsClearLoading(true);
        try {
            await API.delete('/admin/logs');
            toast.success('✅ Logs cleared successfully');
            setLogs([]);
            setLogsTotal(0);
            setLogsPages(1);
        } catch { toast.error('Failed to clear logs'); }
        finally { setLogsClearLoading(false); }
    };

    useEffect(() => { if (tab === 'logs') loadLogs(); }, [tab, loadLogs]);

    const loadOrders = useCallback(async () => {
        setOrdersLoading(true);
        try {
            const res = await API.get(`/admin/orders?page=${ordersPage}&limit=10&status=${ordersFilter}`);
            if (res.data.success) {
                setOrders(res.data.data);
                setOrdersTotal(res.data.total);
                setOrdersPages(res.data.pages);
                setOrdersRevenue(res.data.totalRevenue || 0);
            }
        } catch {
            toast.error('Failed to load orders');
        } finally {
            setOrdersLoading(false);
        }
    }, [ordersPage, ordersFilter]);

    const handleUpdateOrderStatus = async (orderId, newStatus) => {
        try {
            const res = await API.put(`/admin/orders/${orderId}/status`, { status: newStatus });
            if (res.data.success) {
                toast.success(res.data.message || 'Order status updated');
                if (selectedOrder && selectedOrder._id === orderId) {
                    setSelectedOrder(res.data.data);
                }
                loadOrders();
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to update order status');
        }
    };

    const handleDeleteOrder = async (orderId) => {
        if (!window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) return;
        try {
            const res = await API.delete(`/admin/orders/${orderId}`);
            if (res.data.success) {
                toast.success('Order deleted successfully');
                loadOrders();
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to delete order');
        }
    };

    useEffect(() => { if (tab === 'orders') loadOrders(); }, [tab, loadOrders]);

    const handleAddUser = async () => {
        const { firstName, email, phone, password } = addUserForm;
        if (!firstName || !email || !phone || !password) return toast.error('Please fill all required fields.');
        if (password.length < 6) return toast.error('Password must be at least 6 characters.');
        setAddUserSaving(true);
        try {
            const res = await API.post('/admin/users', addUserForm);
            toast.success(res.data.message || 'User created!');
            setShowAddUserModal(false);
            setAddUserForm({ firstName: '', lastName: '', email: '', phone: '', password: '', grantSubscription: false });
            loadUsers(); loadDashboard();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to create user.');
        } finally { setAddUserSaving(false); }
    };

    // ── Handle Send Invitation ────────────────────────────────────────────────
    const handleSendInvite = async () => {
        const { email } = inviteForm;
        if (!email) return toast.error('Please enter the customer\'s email address.');
        setInviteSending(true);
        try {
            const res = await API.post('/admin/invite', { email: inviteForm.email, grantSubscription: inviteForm.grantSubscription });
            toast.success(`✉️ Invitation sent to ${res.data.data?.email || email}!`);
            setShowInviteModal(false);
            setInviteForm({ email: '', grantSubscription: true });
            loadUsers(); loadDashboard();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to send invitation.');
        } finally { setInviteSending(false); }
    };

    const handleDeleteUser = async (id, name) => {
        if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
        try { await API.delete(`/admin/users/${id}`); toast.success('User deleted'); loadUsers(); loadDashboard(); }
        catch { toast.error('Failed to delete user'); }
    };

    const handleToggleSubscription = async (userId, activate) => {
        try {
            await API.patch(`/admin/users/${userId}/subscription`, { active: activate });
            toast.success(activate ? '✅ Subscription activated' : '❌ Subscription revoked');
            const res = await API.get(`/admin/users/${userId}`);
            setSelectedUser(res.data.data);
            loadUsers(); loadDashboard();
        } catch (err) { toast.error(err.response?.data?.message || 'Action failed'); }
    };

    const openUserDetail = async (u) => {
        setSelectedUser(u);
        setUserDetailLoading(true);
        setUserDetailReports([]);
        try {
            const res = await API.get(`/admin/users/${u._id}/reports`);
            // Enrich each raw report with computed numerology values
            const enriched = (res.data.data || []).map(r => ({
                ...r,
                ...calculateAll({ dob: r.dob, gender: r.gender }),
            }));
            setUserDetailReports(enriched);
        } catch { /* ignore */ }
        finally { setUserDetailLoading(false); }
    };

    const handleSavePricing = async () => {
        setPricingSaving(true);
        try {
            const res = await API.put('/admin/settings', pricingForm);
            if (res.data.success) {
                toast.success('✅ Pricing updated successfully!');
                setPricingForm(res.data.data);
            }
        } catch { toast.error('Failed to save settings'); } finally { setPricingSaving(false); }
    };

    const handleSaveCoupon = async (formData) => {
        try {
            if (couponModal?._id) {
                await API.put(`/admin/coupons/${couponModal._id}`, formData);
                toast.success('✅ Coupon updated!');
            } else {
                await API.post('/admin/coupons', formData);
                toast.success('✅ Coupon created!');
            }
            setCouponModal(null);
            loadCoupons();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save coupon.');
            throw err;
        }
    };

    const handleSaveBracelet = async (formData) => {
        try {
            if (braceletModal?._id) {
                await API.put(`/admin/bracelets/${braceletModal._id}`, formData);
                toast.success('✅ Bracelet product updated!');
            } else {
                await API.post('/admin/bracelets', formData);
                toast.success('✅ Bracelet product saved!');
            }
            setBraceletModal(null);
            loadBracelets();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save bracelet product.');
        }
    };

    const handleDeleteBracelet = async (id) => {
        if (!window.confirm('Delete this bracelet product?')) return;
        setDeletingBracelet(id);
        try {
            await API.delete(`/admin/bracelets/${id}`);
            toast.success('Bracelet product deleted.');
            loadBracelets();
        } catch { toast.error('Failed to delete.'); } finally { setDeletingBracelet(null); }
    };

    const handleSaveCommonProduct = async (formData) => {
        try {
            if (formData._id) {
                await API.put(`/common-products/${formData._id}`, formData);
                toast.success('✅ Product updated!');
            } else {
                await API.post('/common-products', formData);
                toast.success('✅ Product created!');
            }
            setCommonProductModal(null);
            loadCommonProducts();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save product');
        }
    };

    const handleDeleteCommonProduct = async (id) => {
        if (!window.confirm('Are you sure you want to delete this product?')) return;
        setDeletingCommonProduct(id);
        try {
            await API.delete(`/common-products/${id}`);
            toast.success('Product deleted.');
            loadCommonProducts();
        } catch { toast.error('Failed to delete.'); } finally { setDeletingCommonProduct(null); }
    };

    const handleDeleteReport = async (id, name) => {
        if (!window.confirm(`Delete report for "${name}"? This cannot be undone.`)) return;
        setDeletingReport(id);
        try {
            await API.delete(`/admin/reports/${id}`);
            toast.success('Report deleted.');
            setExpandedReport(null);
            loadReports(); loadDashboard();
        } catch { toast.error('Failed to delete report.'); } finally { setDeletingReport(null); }
    };

    const handleDeleteReportsByDate = async () => {
        if (!deleteStartDate && !deleteEndDate) {
            toast.error('Please select at least a start or end date.');
            return;
        }

        const dateRangeStr = deleteStartDate && deleteEndDate
            ? `between ${deleteStartDate} and ${deleteEndDate}`
            : deleteStartDate
                ? `since ${deleteStartDate}`
                : `up to ${deleteEndDate}`;

        if (!window.confirm(`Are you sure you want to permanently delete all customer reports ${dateRangeStr}? This action is irreversible.`)) {
            return;
        }

        setDeletingByDate(true);
        try {
            const res = await API.delete(`/admin/reports-by-date?startDate=${deleteStartDate}&endDate=${deleteEndDate}`);
            if (res.data.success) {
                toast.success(res.data.message || `Successfully deleted ${res.data.deletedCount} report(s).`);
                setDeleteStartDate('');
                setDeleteEndDate('');
                loadReports();
                loadDashboard();
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to delete reports by date.');
        } finally {
            setDeletingByDate(false);
        }
    };

    const loadDuplicates = async () => {
        setDuplicatesLoading(true);
        try {
            const res = await API.get('/admin/reports/duplicates');
            if (res.data.success) setDuplicates(res.data.data);
        } catch { toast.error('Failed to load duplicates'); }
        finally { setDuplicatesLoading(false); }
    };

    const handleDeleteAllDuplicates = async () => {
        const totalDups = duplicates.reduce((s, g) => s + g.duplicateIds.length, 0);
        if (!window.confirm(`Delete ${totalDups} duplicate report(s)? The oldest copy of each group will be kept.`)) return;
        setDeletingDuplicates(true);
        try {
            const res = await API.delete('/admin/reports/duplicates');
            toast.success(res.data.message);
            loadDuplicates(); loadReports(); loadDashboard();
        } catch { toast.error('Failed to delete duplicates.'); }
        finally { setDeletingDuplicates(false); }
    };

    const handleDeleteSingleDupReport = async (id, name) => {
        if (!window.confirm(`Delete this duplicate report for "${name}"?`)) return;
        setDeletingDupReport(id);
        try {
            await API.delete(`/admin/reports/${id}`);
            toast.success('Duplicate deleted.');
            loadDuplicates(); loadReports(); loadDashboard();
        } catch { toast.error('Failed to delete.'); }
        finally { setDeletingDupReport(null); }
    };

    const handleDeleteCoupon = async (id) => {
        if (!window.confirm('Delete this coupon? This cannot be undone.')) return;
        setDeletingCoupon(id);
        try {
            await API.delete(`/admin/coupons/${id}`);
            toast.success('Coupon deleted.');
            loadCoupons();
        } catch { toast.error('Failed to delete coupon.'); } finally { setDeletingCoupon(null); }
    };

    const handleToggleCoupon = async (coupon) => {
        try {
            await API.put(`/admin/coupons/${coupon._id}`, { isActive: !coupon.isActive });
            toast.success(`Coupon ${!coupon.isActive ? 'activated' : 'deactivated'}.`);
            loadCoupons();
        } catch { toast.error('Failed to update coupon.'); }
    };

    const pieData = stats ? [
        { name: 'Weekly New', value: stats.weeklyUsers, color: T.indigo },
        { name: 'Older', value: Math.max(0, stats.totalUsers - stats.weeklyUsers), color: '#e0e7ff' },
    ] : [];

    const Avatar = ({ first = '?', last = '?', size = 36 }) => {
        const palette = [T.indigo, T.gold, T.teal, T.pink, T.emerald, T.orange];
        const colour = palette[(first.charCodeAt(0) + last.charCodeAt(0)) % palette.length];
        return (
            <div style={{ width: size, height: size, borderRadius: '50%', background: `linear-gradient(135deg,${colour},${colour}bb)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.35, fontWeight: 800, color: '#fff', flexShrink: 0, boxShadow: `0 2px 8px ${colour}44` }}>
                {first[0]}{last[0]}
            </div>
        );
    };

    const SectionLoader = () => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60, gap: 12 }}>
            <div style={{ width: 20, height: 20, borderRadius: '50%', border: `3px solid ${T.indigoLt}`, borderTopColor: T.indigo, animation: 'spin 0.8s linear infinite' }} />
            <span style={{ color: T.textMute, fontSize: 14 }}>Loading…</span>
        </div>
    );

    const thTd = { padding: '13px 16px', borderBottom: `1px solid ${T.border}`, fontSize: 13, color: T.textSub };
    const thSt = { ...thTd, fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.7, color: T.textMute, background: T.bg };

    return (
        <div style={{ minHeight: '100vh', background: T.bg, paddingTop: 90, paddingBottom: 60 }}>
            <style>{`
        @keyframes liveDot { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.4;transform:scale(0.7)} }
        @keyframes liveRing { 0%{transform:scale(1);opacity:1} 100%{transform:scale(2.2);opacity:0} }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
        .al-row:hover td { background: #f8faff !important; }
        .al-expand td { padding:0 !important; border:none !important; }
      `}</style>

            {couponModal !== null && (
                <CouponModal
                    coupon={couponModal._id ? couponModal : null}
                    onClose={() => setCouponModal(null)}
                    onSave={handleSaveCoupon}
                    T={T}
                />
            )}

            {/* Order Detail Modal */}
            {selectedOrder !== null && (
                <OrderDetailModal
                    order={selectedOrder}
                    onClose={() => setSelectedOrder(null)}
                    onUpdateStatus={handleUpdateOrderStatus}
                    T={T}
                />
            )}

            {/* ── Invite User Modal ───────────────────────────────── */}
            {showInviteModal && (
                <>
                    <div onClick={() => setShowInviteModal(false)} style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
                        zIndex: 2000, backdropFilter: 'blur(4px)', animation: 'fadeIn 0.2s ease',
                    }} />
                    <div style={{
                        position: 'fixed', top: '50%', left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '100%', maxWidth: 440,
                        background: T.surface, borderRadius: 20,
                        boxShadow: '0 24px 80px rgba(0,0,0,0.28)',
                        zIndex: 2001, padding: '28px 32px',
                        animation: 'fadeUp 0.25s ease',
                    }}>
                        {/* Header */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                            <div>
                                <h2 style={{ fontSize: 20, fontWeight: 800, color: T.text, marginBottom: 2 }}>✉️ Send Email Invitation</h2>
                                <p style={{ fontSize: 13, color: T.textMute }}>Enter the email — customer fills the rest</p>
                            </div>
                            <button onClick={() => setShowInviteModal(false)} style={{ background: 'none', border: `1.5px solid ${T.border}`, borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: T.textMute, display: 'flex', alignItems: 'center' }}>
                                <FiX size={16} />
                            </button>
                        </div>

                        {/* Info banner */}
                        <div style={{ background: isDark ? 'rgba(108,63,197,0.12)' : '#f3f0ff', border: `1px solid ${isDark ? 'rgba(108,63,197,0.3)' : '#c4b5fd'}`, borderRadius: 12, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: isDark ? '#c4b5fd' : '#6c3fc5', lineHeight: 1.6 }}>
                            📩 Customer will receive a magic link to <strong>set their own name, phone &amp; password</strong>. Link valid for <strong>48 hours</strong>.
                        </div>

                        {/* Email only */}
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ fontSize: 11, fontWeight: 700, color: T.textMute, textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', marginBottom: 8 }}>Customer Email *</label>
                            <input
                                id="invite-email-input"
                                type="email"
                                placeholder="customer@example.com"
                                value={inviteForm.email}
                                onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))}
                                onKeyDown={e => e.key === 'Enter' && handleSendInvite()}
                                autoFocus
                                style={{ width: '100%', padding: '13px 16px', background: T.bg, border: `1.5px solid ${T.border}`, borderRadius: 10, color: T.text, fontSize: 15, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                                onFocus={e => e.target.style.borderColor = '#6c3fc5'}
                                onBlur={e => e.target.style.borderColor = T.border}
                            />
                        </div>

                        {/* Grant Subscription Toggle */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: T.bg, borderRadius: 12, border: `1.5px solid ${T.border}`, marginBottom: 24 }}>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 14, color: T.text }}>Grant Paid Subscription</div>
                                <div style={{ fontSize: 11, color: T.textMute }}>Give this customer full premium access</div>
                            </div>
                            <div
                                onClick={() => setInviteForm(f => ({ ...f, grantSubscription: !f.grantSubscription }))}
                                style={{ width: 46, height: 26, borderRadius: 50, cursor: 'pointer', transition: 'background 0.2s', background: inviteForm.grantSubscription ? T.emerald : T.border, position: 'relative', flexShrink: 0 }}
                            >
                                <div style={{ position: 'absolute', top: 3, left: inviteForm.grantSubscription ? 23 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} />
                            </div>
                        </div>

                        {/* Footer Buttons */}
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button onClick={() => setShowInviteModal(false)}
                                style={{ flex: 1, padding: '12px', borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.surface, color: T.textSub, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                                Cancel
                            </button>
                            <button onClick={handleSendInvite} disabled={inviteSending}
                                style={{ flex: 2, padding: '12px', borderRadius: 10, border: 'none', background: inviteSending ? '#6c3fc588' : '#6c3fc5', color: '#fff', fontWeight: 700, fontSize: 14, cursor: inviteSending ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 4px 14px rgba(108,63,197,0.35)', transition: 'all 0.2s' }}>
                                {inviteSending ? <><div style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid #ffffff66', borderTopColor: '#fff', animation: 'spin 0.7s linear infinite' }} />Sending…</> : '✉️ Send Invitation'}
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* ── Add User Modal ─────────────────────────────────── */}
            {showAddUserModal && (
                <>
                    <div onClick={() => setShowAddUserModal(false)} style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
                        zIndex: 2000, backdropFilter: 'blur(4px)', animation: 'fadeIn 0.2s ease',
                    }} />
                    <div style={{
                        position: 'fixed', top: '50%', left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '100%', maxWidth: 480,
                        background: T.surface, borderRadius: 20,
                        boxShadow: '0 24px 80px rgba(0,0,0,0.28)',
                        zIndex: 2001, padding: '28px 32px',
                        animation: 'fadeUp 0.25s ease',
                    }}>
                        {/* Header */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                            <div>
                                <h2 style={{ fontSize: 20, fontWeight: 800, color: T.text, marginBottom: 2 }}>➕ Add User Manually</h2>
                                <p style={{ fontSize: 13, color: T.textMute }}>Create a new user account directly from admin</p>
                            </div>
                            <button onClick={() => setShowAddUserModal(false)} style={{ background: 'none', border: `1.5px solid ${T.border}`, borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: T.textMute, display: 'flex', alignItems: 'center' }}>
                                <FiX size={16} />
                            </button>
                        </div>

                        {/* Form */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                {[
                                    { key: 'firstName', label: 'First Name *', placeholder: 'Rahul' },
                                    { key: 'lastName', label: 'Last Name', placeholder: 'Sharma' },
                                ].map(({ key, label, placeholder }) => (
                                    <div key={key}>
                                        <label style={{ fontSize: 11, fontWeight: 700, color: T.textMute, textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', marginBottom: 6 }}>{label}</label>
                                        <input
                                            placeholder={placeholder}
                                            value={addUserForm[key]}
                                            onChange={e => setAddUserForm(f => ({ ...f, [key]: e.target.value }))}
                                            style={{ width: '100%', padding: '11px 14px', background: T.bg, border: `1.5px solid ${T.border}`, borderRadius: 10, color: T.text, fontSize: 14, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                                            onFocus={e => e.target.style.borderColor = T.indigo}
                                            onBlur={e => e.target.style.borderColor = T.border}
                                        />
                                    </div>
                                ))}
                            </div>

                            {[
                                { key: 'email', label: 'Email *', placeholder: 'rahul@example.com', type: 'email' },
                                { key: 'phone', label: 'Phone *', placeholder: '9876543210', type: 'tel' },
                                { key: 'password', label: 'Password *', placeholder: 'Min 6 characters', type: 'password' },
                            ].map(({ key, label, placeholder, type }) => (
                                <div key={key}>
                                    <label style={{ fontSize: 11, fontWeight: 700, color: T.textMute, textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', marginBottom: 6 }}>{label}</label>
                                    <input
                                        type={type}
                                        placeholder={placeholder}
                                        value={addUserForm[key]}
                                        onChange={e => setAddUserForm(f => ({ ...f, [key]: e.target.value }))}
                                        style={{ width: '100%', padding: '11px 14px', background: T.bg, border: `1.5px solid ${T.border}`, borderRadius: 10, color: T.text, fontSize: 14, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                                        onFocus={e => e.target.style.borderColor = T.indigo}
                                        onBlur={e => e.target.style.borderColor = T.border}
                                    />
                                </div>
                            ))}

                            {/* Grant Subscription Toggle */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: T.bg, borderRadius: 12, border: `1.5px solid ${T.border}` }}>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: 14, color: T.text }}>Grant Paid Subscription</div>
                                    <div style={{ fontSize: 11, color: T.textMute }}>Mark this user as a paid subscriber immediately</div>
                                </div>
                                <div
                                    onClick={() => setAddUserForm(f => ({ ...f, grantSubscription: !f.grantSubscription }))}
                                    style={{
                                        width: 46, height: 26, borderRadius: 50, cursor: 'pointer', transition: 'background 0.2s',
                                        background: addUserForm.grantSubscription ? T.emerald : T.border,
                                        position: 'relative', flexShrink: 0,
                                    }}
                                >
                                    <div style={{
                                        position: 'absolute', top: 3,
                                        left: addUserForm.grantSubscription ? 23 : 3,
                                        width: 20, height: 20, borderRadius: '50%',
                                        background: '#fff', transition: 'left 0.2s',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                                    }} />
                                </div>
                            </div>
                        </div>

                        {/* Footer Buttons */}
                        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
                            <button onClick={() => setShowAddUserModal(false)}
                                style={{ flex: 1, padding: '12px', borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.surface, color: T.textSub, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                                Cancel
                            </button>
                            <button onClick={handleAddUser} disabled={addUserSaving}
                                style={{ flex: 2, padding: '12px', borderRadius: 10, border: 'none', background: addUserSaving ? T.emerald + '88' : T.emerald, color: '#fff', fontWeight: 700, fontSize: 14, cursor: addUserSaving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 4px 14px rgba(16,185,129,0.35)', transition: 'all 0.2s' }}>
                                {addUserSaving ? <><div style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid #ffffff66', borderTopColor: '#fff', animation: 'spin 0.7s linear infinite' }} /> Creating…</> : '✅ Create User'}
                            </button>
                        </div>
                    </div>
                </>
            )}

            <div style={{ maxWidth: 1550, margin: '0 auto', padding: '0 24px' }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 14 }}>
                    <div>
                        <h1 style={{ fontSize: 26, fontWeight: 900, color: T.text, marginBottom: 3, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                            Admin Console
                        </h1>
                        <p style={{ color: T.textMute, fontSize: 13 }}>
                            Welcome, <span style={{ color: T.indigo, fontWeight: 600 }}>{user?.firstName}</span> — Full platform control
                        </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <LivePulse count={liveCount} connected={socketConnected} />
                        {/* Theme Toggle */}
                        <button
                            onClick={toggleTheme}
                            title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                padding: '9px 16px', borderRadius: 10,
                                border: `1.5px solid ${T.border}`,
                                background: T.surface, color: T.textSub,
                                fontWeight: 600, fontSize: 13, cursor: 'pointer',
                                transition: 'all 0.2s',
                            }}
                        >
                            {isDark ? '☀️ Light' : '🌙 Dark'}
                        </button>
                        <button onClick={loadDashboard} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.surface, color: T.textSub, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                            <FiRefreshCw size={13} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /> Refresh
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div style={{ display: 'flex', gap: 22, alignItems: 'flex-start' }}>
                    <SideNav tab={tab} setTab={setTab} stats={stats} liveCount={liveCount} />

                    <div style={{ flex: 1, minWidth: 0, animation: 'fadeUp 0.35s ease' }}>

                        {/* ══ DASHBOARD ══════════════════════════════════════════════ */}
                        {tab === 'dashboard' && (
                            <div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(185px,1fr))', gap: 16, marginBottom: 24 }}>
                                    {/* <StatCard icon="🟢" label="Live Visitors" value={liveCount} colour={T.emerald} colourLt={T.emeraldLt} sub="Right now" animated /> */}
                                    <StatCard icon="👥" label="Total Users" value={stats?.totalUsers} colour={T.indigo} colourLt={T.indigoLt} sub="Registered" animated trend={stats?.weeklyUsers > 0 ? Math.round((stats.weeklyUsers / Math.max(stats.totalUsers - stats.weeklyUsers, 1)) * 100) : 0} />
                                    <StatCard icon="💳" label="Paid Users" value={stats?.paidUsers} colour={T.emerald} colourLt={T.emeraldLt} sub="Active subscriptions" animated />
                                    <StatCard icon="💰" label="Total Revenue" value={stats?.totalRevenue} colour={T.gold} colourLt={T.goldLt} sub="Calculated revenue" animated prefix="₹" />
                                    <StatCard icon="📋" label="Total Reports" value={stats?.totalReports} colour={T.teal} colourLt={T.tealLt} sub="Generated" animated />
                                    <StatCard icon="📅" label="This Week" value={stats?.weeklyUsers} colour={T.pink} colourLt={T.pinkLt} sub="New registrations" animated />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 20 }}>
                                    <LCard>
                                        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 20, color: T.indigo, display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <FiTrendingUp size={16} /> Weekly Registrations & Reports
                                        </h3>
                                        <ResponsiveContainer width="100%" height={220}>
                                            <AreaChart data={weekly}>
                                                <defs>
                                                    <linearGradient id="gu" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor={T.indigo} stopOpacity={0.18} />
                                                        <stop offset="95%" stopColor={T.indigo} stopOpacity={0} />
                                                    </linearGradient>
                                                    <linearGradient id="gr" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor={T.gold} stopOpacity={0.18} />
                                                        <stop offset="95%" stopColor={T.gold} stopOpacity={0} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                                                <XAxis dataKey="label" tick={{ fill: T.textMute, fontSize: 11 }} axisLine={false} tickLine={false} />
                                                <YAxis tick={{ fill: T.textMute, fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                                                <Tooltip content={<CustomTooltip />} />
                                                <Legend iconType="circle" wrapperStyle={{ color: T.textSub, fontSize: 12 }} />
                                                <Area type="monotone" dataKey="users" stroke={T.indigo} strokeWidth={2.5} fill="url(#gu)" name="Users" dot={{ fill: T.indigo, r: 3 }} />
                                                <Area type="monotone" dataKey="reports" stroke={T.gold} strokeWidth={2.5} fill="url(#gr)" name="Reports" dot={{ fill: T.gold, r: 3 }} />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </LCard>

                                    <LCard style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, color: T.gold, alignSelf: 'flex-start' }}>User Share</h3>
                                        <PieChart width={180} height={180}>
                                            <Pie data={pieData} cx={85} cy={85} innerRadius={55} outerRadius={80} paddingAngle={4} dataKey="value">
                                                {pieData.map((e, i) => <Cell key={i} fill={e.color} stroke="none" />)}
                                            </Pie>
                                            <Tooltip contentStyle={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 10 }} />
                                        </PieChart>
                                        <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                                            {[{ c: T.indigo, l: 'New (week)' }, { c: '#e0e7ff', l: 'Existing' }].map(x => (
                                                <div key={x.l} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.textSub }}>
                                                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: x.c, border: `1px solid ${T.border}` }} />{x.l}
                                                </div>
                                            ))}
                                        </div>
                                        <div style={{ marginTop: 12, textAlign: 'center' }}>
                                            <p style={{ fontSize: 32, fontWeight: 900, color: T.indigo }}>{stats?.totalUsers ?? 0}</p>
                                            <p style={{ fontSize: 11, color: T.textMute }}>Total Users</p>
                                        </div>
                                    </LCard>
                                </div>

                                <LCard>
                                    <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 20, color: T.gold, display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <FiCalendar size={16} /> Monthly Overview (Last 4 Weeks)
                                    </h3>
                                    <ResponsiveContainer width="100%" height={200}>
                                        <BarChart data={monthly} barGap={8}>
                                            <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                                            <XAxis dataKey="label" tick={{ fill: T.textMute, fontSize: 11 }} axisLine={false} tickLine={false} />
                                            <YAxis tick={{ fill: T.textMute, fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend iconType="circle" wrapperStyle={{ color: T.textSub, fontSize: 12 }} />
                                            <Bar dataKey="users" fill={T.indigo} radius={[6, 6, 0, 0]} name="Users" maxBarSize={48} />
                                            <Bar dataKey="reports" fill={T.gold} radius={[6, 6, 0, 0]} name="Reports" maxBarSize={48} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </LCard>
                            </div>
                        )}

                        {/* ══ ANALYTICS ══════════════════════════════════════════════ */}
                        {tab === 'analytics' && (
                            <div>
                                <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                                    {['weekly', 'monthly'].map(v => (
                                        <button key={v} onClick={() => setAnalyticsView(v)} style={{
                                            padding: '9px 20px', borderRadius: 10, border: `1.5px solid ${analyticsView === v ? T.indigo : T.border}`,
                                            background: analyticsView === v ? T.indigo : T.surface, color: analyticsView === v ? '#fff' : T.textSub,
                                            fontWeight: 600, fontSize: 13, cursor: 'pointer', transition: 'all 0.18s',
                                            boxShadow: analyticsView === v ? '0 4px 14px rgba(79,110,247,0.3)' : 'none'
                                        }}>
                                            {v === 'weekly' ? '📅 Week View' : '📆 Month View'}
                                        </button>
                                    ))}
                                </div>
                                <LCard style={{ marginBottom: 20 }}>
                                    <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 24, color: T.indigo }}>
                                        {analyticsView === 'weekly' ? '📅 Daily Registrations & Reports (Last 7 Days)' : '📆 Weekly Buckets (Last 30 Days)'}
                                    </h3>
                                    <ResponsiveContainer width="100%" height={320}>
                                        <LineChart data={analyticsView === 'weekly' ? weekly : monthly}>
                                            <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                                            <XAxis dataKey="label" tick={{ fill: T.textMute, fontSize: 12 }} axisLine={false} tickLine={false} />
                                            <YAxis tick={{ fill: T.textMute, fontSize: 12 }} axisLine={false} tickLine={false} allowDecimals={false} />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend iconType="circle" wrapperStyle={{ color: T.textSub, fontSize: 12 }} />
                                            <Line type="monotone" dataKey="users" stroke={T.indigo} strokeWidth={3} dot={{ fill: T.indigo, r: 5, strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 7 }} name="Users" />
                                            <Line type="monotone" dataKey="reports" stroke={T.gold} strokeWidth={3} dot={{ fill: T.gold, r: 5, strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 7 }} name="Reports" />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </LCard>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 16 }}>
                                    {[
                                        { label: 'Week Registrations', value: stats?.weeklyUsers ?? 0, colour: T.indigo, colourLt: T.indigoLt, icon: '📅' },
                                        { label: 'Month Registrations', value: stats?.monthlyUsers ?? 0, colour: T.gold, colourLt: T.goldLt, icon: '📆' },
                                        { label: 'Month Reports', value: stats?.monthlyReports ?? 0, colour: T.teal, colourLt: T.tealLt, icon: '📝' },
                                        { label: 'Total Users', value: stats?.totalUsers ?? 0, colour: T.pink, colourLt: T.pinkLt, icon: '👥' },
                                    ].map(c => (
                                        <LCard key={c.label} style={{ padding: 20 }}>
                                            <div style={{ width: 40, height: 40, borderRadius: 10, background: c.colourLt, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, marginBottom: 10 }}>{c.icon}</div>
                                            <p style={{ color: T.textMute, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 700 }}>{c.label}</p>
                                            <p style={{ fontSize: 36, fontWeight: 900, color: c.colour, marginTop: 4, lineHeight: 1 }}>{c.value}</p>
                                        </LCard>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* ══ USERS ══════════════════════════════════════════════════ */}
                        {tab === 'users' && (
                            <div>
                                {/* ── Search + Action Row ── */}
                                <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
                                        <FiSearch style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: T.textMute, fontSize: 15 }} />
                                        <input ref={searchRef} placeholder="Search by name, email, phone…"
                                            style={{ width: '100%', paddingLeft: 42, padding: '11px 16px 11px 42px', background: T.surface, border: `1.5px solid ${T.border}`, borderRadius: 10, color: T.text, fontSize: 14, outline: 'none', fontFamily: 'inherit' }}
                                            value={usersSearch}
                                            onChange={e => { setUsersSearch(e.target.value); setUsersPage(1); }}
                                            onKeyDown={e => e.key === 'Enter' && loadUsers()}
                                            onFocus={e => e.target.style.borderColor = T.indigo}
                                            onBlur={e => e.target.style.borderColor = T.border} />
                                    </div>
                                    <button onClick={loadUsers} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '11px 20px', borderRadius: 10, border: 'none', background: T.indigo, color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', boxShadow: '0 4px 14px rgba(79,110,247,0.3)' }}>
                                        <FiSearch size={13} /> Search
                                    </button>
                                    <button onClick={() => setShowAddUserModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '11px 20px', borderRadius: 10, border: 'none', background: T.emerald, color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', boxShadow: '0 4px 14px rgba(16,185,129,0.3)', flexShrink: 0 }}>
                                        ＋ Add User
                                    </button>
                                    <button
                                        onClick={() => setShowInviteModal(true)}
                                        id="send-invite-btn"
                                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '11px 20px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#6c3fc5,#4a1fa8)', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', boxShadow: '0 4px 14px rgba(108,63,197,0.35)', flexShrink: 0 }}>
                                        ✉️ Send Invite
                                    </button>
                                </div>

                                {/* ── Filter + Sort Row ── */}
                                <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                                    {/* Subscription filter pills */}
                                    <div style={{ display: 'flex', gap: 6 }}>
                                        {[['all', '🌐 All'], ['paid', '💳 Paid'], ['free', '🔓 Free']].map(([val, lbl]) => (
                                            <button key={val} onClick={() => { setUsersFilter(val); setUsersPage(1); }}
                                                style={{
                                                    padding: '7px 16px', borderRadius: 50, fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 0.18s',
                                                    border: usersFilter === val ? 'none' : `1.5px solid ${T.border}`,
                                                    background: usersFilter === val ? (val === 'paid' ? T.emerald : val === 'free' ? T.gold : T.indigo) : T.surface,
                                                    color: usersFilter === val ? '#fff' : T.textSub,
                                                    boxShadow: usersFilter === val ? '0 2px 10px rgba(0,0,0,0.15)' : 'none',
                                                }}>{lbl}
                                            </button>
                                        ))}
                                    </div>
                                    {/* Sort dropdown buttons */}
                                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                        <span style={{ fontSize: 11, color: T.textMute, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Sort:</span>
                                        {[['newest', '🕒 Newest'], ['oldest', '📅 Oldest'], ['reports', '📋 Most Reports']].map(([val, lbl]) => (
                                            <button key={val} onClick={() => { setUsersSort(val); setUsersPage(1); }}
                                                style={{
                                                    padding: '7px 13px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.18s',
                                                    border: usersSort === val ? `1.5px solid ${T.indigo}` : `1.5px solid ${T.border}`,
                                                    background: usersSort === val ? T.indigoLt : T.surface,
                                                    color: usersSort === val ? T.indigo : T.textSub,
                                                }}>{lbl}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <LCard style={{ padding: 0, overflow: 'hidden' }}>
                                    <div style={{ padding: '14px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: T.bg }}>
                                        <span style={{ fontSize: 13, fontWeight: 700, color: T.text, display: 'flex', alignItems: 'center', gap: 7 }}>
                                            <FiUsers size={15} style={{ color: T.indigo }} />{usersTotal} registered user{usersTotal !== 1 ? 's' : ''}
                                        </span>
                                        <button onClick={loadUsers} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8, border: `1.5px solid ${T.border}`, background: T.surface, color: T.textSub, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                                            <FiRefreshCw size={12} /> Reload
                                        </button>
                                    </div>
                                    {usersLoading ? <SectionLoader /> : (
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <thead>
                                                    <tr>{['#', 'User', 'Email', 'Phone', 'Joined', 'Subscribed', 'Reports', 'Actions'].map(h => <th key={h} style={thSt}>{h}</th>)}</tr>
                                                </thead>
                                                <tbody>
                                                    {users.map((u, i) => (
                                                        <tr key={u._id} className="al-row">
                                                            <td style={{ ...thTd, color: T.textMute, fontSize: 12 }}>{(usersPage - 1) * 10 + i + 1}</td>
                                                            <td style={thTd}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                    <Avatar first={u.firstName} last={u.lastName} size={34} />
                                                                    <div>
                                                                        <div style={{ color: T.text, fontWeight: 700, fontSize: 14 }}>{u.firstName} {u.lastName}</div>
                                                                        {u.role === 'admin' && <span style={{ fontSize: 10, color: T.gold, fontWeight: 700 }}>ADMIN</span>}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td style={thTd}>{u.email}</td>
                                                            <td style={thTd}>{u.phone}</td>
                                                            <td style={{ ...thTd, color: T.textMute, fontSize: 12 }}>{new Date(u.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                                                            <td style={thTd}>
                                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 12px', borderRadius: 50, fontSize: 12, fontWeight: 700, background: u.subscription?.active ? '#dcfce7' : '#f1f5f9', color: u.subscription?.active ? '#16a34a' : '#64748b' }}>
                                                                    {u.subscription?.active ? <FiCheckCircle size={11} /> : <FiAlertCircle size={11} />}
                                                                    {u.subscription?.active ? 'Paid' : 'Free'}
                                                                </span>
                                                            </td>
                                                            <td style={thTd}><span style={{ fontSize: 13, color: T.indigo, fontWeight: 700 }}>{u.reportCount ?? '—'}</span></td>
                                                            <td style={thTd}>
                                                                <div style={{ display: 'flex', gap: 7 }}>
                                                                    <button
                                                                        style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 13px', borderRadius: 8, border: `1.5px solid ${T.indigo}30`, background: T.indigoLt, color: T.indigo, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
                                                                        onClick={() => openUserDetail(u)}>
                                                                        <FiEye size={12} /> View
                                                                    </button>
                                                                    <button style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 13px', borderRadius: 8, border: '1.5px solid #fecaca', background: '#fff5f5', color: '#dc2626', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
                                                                        onClick={() => handleDeleteUser(u._id, `${u.firstName} ${u.lastName}`)}>
                                                                        <FiTrash2 size={12} /> Delete
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {users.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', padding: 48, color: T.textMute }}><FiUsers size={32} style={{ display: 'block', margin: '0 auto 8px', opacity: 0.4 }} />No users found</td></tr>}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                    <Pagination page={usersPage} pages={usersPages} onPage={p => setUsersPage(p)} />
                                </LCard>
                            </div>
                        )}

                        {/* ══ USER DETAIL SLIDE-OVER ══════════════════════════════════ */}
                        {selectedUser && (
                            <>
                                {/* Backdrop */}
                                <div onClick={() => setSelectedUser(null)} style={{
                                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
                                    backdropFilter: 'blur(3px)', animation: 'fadeIn 0.2s ease',
                                }} />
                                {/* Panel */}
                                <div style={{
                                    position: 'fixed', top: 0, right: 0, bottom: 0, width: '100%', maxWidth: 520,
                                    background: T.surface, zIndex: 1001, overflowY: 'auto',
                                    boxShadow: '-8px 0 40px rgba(0,0,0,0.2)',
                                    animation: 'slideIn 0.28s ease',
                                    display: 'flex', flexDirection: 'column',
                                }}>
                                    <style>{`
                                        @keyframes fadeIn { from{opacity:0} to{opacity:1} }
                                        @keyframes slideIn { from{transform:translateX(100%)} to{transform:translateX(0)} }
                                    `}</style>

                                    {/* Header */}
                                    <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: T.bg, flexShrink: 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <div style={{ width: 44, height: 44, borderRadius: '50%', background: `linear-gradient(135deg, ${T.indigo}, #818cf8)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 900, color: '#fff' }}>
                                                {selectedUser.firstName?.[0]?.toUpperCase()}{selectedUser.lastName?.[0]?.toUpperCase()}
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: 800, fontSize: 16, color: T.text }}>{selectedUser.firstName} {selectedUser.lastName}</div>
                                                <div style={{ fontSize: 12, color: T.textMute }}>{selectedUser.email}</div>
                                            </div>
                                        </div>
                                        <button onClick={() => setSelectedUser(null)} style={{ background: T.surface, border: `1.5px solid ${T.border}`, borderRadius: 8, padding: '7px 10px', cursor: 'pointer', color: T.textMute, display: 'flex', alignItems: 'center' }}>
                                            <FiX size={16} />
                                        </button>
                                    </div>

                                    {/* Subscription Status Banner */}
                                    <div style={{ margin: '20px 24px 0', padding: '16px 20px', borderRadius: 14, background: selectedUser.subscription?.active ? '#dcfce7' : '#fef3c7', border: `1.5px solid ${selectedUser.subscription?.active ? '#86efac' : '#fde68a'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <span style={{ fontSize: 22 }}>{selectedUser.subscription?.active ? '✅' : '🔒'}</span>
                                            <div>
                                                <div style={{ fontWeight: 800, fontSize: 14, color: selectedUser.subscription?.active ? '#15803d' : '#92400e' }}>
                                                    {selectedUser.subscription?.active ? 'Active Subscription' : 'No Subscription'}
                                                </div>
                                                {selectedUser.subscription?.active && selectedUser.subscription?.paidAt && (
                                                    <div style={{ fontSize: 12, color: '#16a34a' }}>Paid on {new Date(selectedUser.subscription.paidAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleToggleSubscription(selectedUser._id, !selectedUser.subscription?.active)}
                                            style={{ padding: '8px 16px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13, background: selectedUser.subscription?.active ? '#dc2626' : T.indigo, color: '#fff', flexShrink: 0 }}>
                                            {selectedUser.subscription?.active ? '❌ Revoke' : '✅ Grant Access'}
                                        </button>
                                    </div>

                                    {/* Details Grid */}
                                    <div style={{ padding: '20px 24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                        {[
                                            { label: 'Phone', value: selectedUser.phone || 'Not provided', icon: '📞' },
                                            { label: 'Role', value: selectedUser.role?.toUpperCase(), icon: '🎭' },
                                            { label: 'Reports', value: userDetailReports.length || selectedUser.reportCount || 0, icon: '📋' },
                                            { label: 'Amount Paid', value: selectedUser.subscription?.amountPaid ? `₹${Number(selectedUser.subscription.amountPaid).toLocaleString('en-IN')}` : '—', icon: '💰' },
                                            { label: 'Registered', value: new Date(selectedUser.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }), icon: '📅' },
                                            { label: 'Order ID', value: selectedUser.subscription?.razorpayOrderId ? selectedUser.subscription.razorpayOrderId.slice(-12) + '…' : '—', icon: '🧾' },
                                        ].map(({ label, value, icon }) => (
                                            <div key={label} style={{ background: T.bg, borderRadius: 12, padding: '14px 16px', border: `1px solid ${T.border}` }}>
                                                <div style={{ fontSize: 11, color: T.textMute, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 5 }}>{icon} {label}</div>
                                                <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{value}</div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Payment ID if exists */}
                                    {selectedUser.subscription?.razorpayPaymentId && (
                                        <div style={{ margin: '0 24px', padding: '12px 16px', background: T.bg, borderRadius: 12, border: `1px solid ${T.border}` }}>
                                            <div style={{ fontSize: 11, color: T.textMute, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>💳 Payment ID</div>
                                            <div style={{ fontSize: 13, fontWeight: 600, color: T.indigo, fontFamily: 'monospace' }}>{selectedUser.subscription.razorpayPaymentId}</div>
                                        </div>
                                    )}

                                    {/* Reports History */}
                                    <div style={{ padding: '20px 24px', flex: 1 }}>
                                        <div style={{ fontWeight: 800, fontSize: 14, color: T.text, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 7 }}>
                                            <FiFileText size={15} style={{ color: T.gold }} />Recent Reports
                                            <span style={{ background: T.indigoLt, color: T.indigo, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 50, marginLeft: 4 }}>{userDetailReports.length}</span>
                                        </div>
                                        {userDetailLoading ? (
                                            <div style={{ textAlign: 'center', padding: 24, color: T.textMute }}>Loading reports…</div>
                                        ) : userDetailReports.length === 0 ? (
                                            <div style={{ textAlign: 'center', padding: 24, color: T.textMute, fontSize: 13 }}>No reports yet</div>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                                {userDetailReports.slice(0, 8).map(r => (
                                                    <div key={r._id} style={{ background: T.bg, borderRadius: 12, padding: '12px 16px', border: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <div>
                                                            <div style={{ fontWeight: 700, fontSize: 14, color: T.text }}>{r.firstName} {r.lastName}</div>
                                                            <div style={{ fontSize: 11, color: T.textMute }}>DOB: {new Date(r.dob).toLocaleDateString('en-IN')} · {r.gender}</div>
                                                        </div>
                                                        <div style={{ textAlign: 'right' }}>
                                                            <div style={{ fontSize: 11, color: T.textMute }}>{new Date(r.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</div>
                                                            <div style={{ display: 'flex', gap: 5, marginTop: 4 }}>
                                                                <span style={{ fontSize: 10, background: '#fffbeb', color: '#92400e', padding: '2px 7px', borderRadius: 50, fontWeight: 700 }}>B:{r.bhagyank}</span>
                                                                <span style={{ fontSize: 10, background: T.indigoLt, color: T.indigo, padding: '2px 7px', borderRadius: 50, fontWeight: 700 }}>M:{r.mulank ?? r.conductor ?? '—'}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                                {userDetailReports.length > 8 && <div style={{ textAlign: 'center', fontSize: 12, color: T.textMute, paddingTop: 4 }}>+{userDetailReports.length - 8} more reports</div>}
                                            </div>
                                        )}
                                    </div>

                                    {/* ── Admin: Change User Password ── */}
                                    <AdminChangePwSection userId={selectedUser._id} userName={`${selectedUser.firstName} ${selectedUser.lastName}`} T={T} />

                                    {/* Footer actions */}
                                    <div style={{ padding: '16px 24px', borderTop: `1px solid ${T.border}`, background: T.bg, flexShrink: 0, display: 'flex', gap: 10 }}>
                                        <button onClick={() => setSelectedUser(null)} style={{ flex: 1, padding: '11px', borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.surface, color: T.textSub, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>Close</button>
                                        <button onClick={() => { setSelectedUser(null); handleDeleteUser(selectedUser._id, `${selectedUser.firstName} ${selectedUser.lastName}`); }}
                                            style={{ padding: '11px 20px', borderRadius: 10, border: '1.5px solid #fecaca', background: '#fff5f5', color: '#dc2626', fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <FiTrash2 size={14} /> Delete User
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* ══ REPORTS ════════════════════════════════════════════════ */}
                        {tab === 'reports' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                {/* Sub-tab switcher */}
                                <div style={{ display: 'flex', gap: 8 }}>
                                    {[['all', '📋 All Reports'], ['duplicates', '⚠️ Duplicates'], ['delete-by-date', '📅 Delete By Date']].map(([key, label]) => (
                                        <button key={key}
                                            onClick={() => { setReportsSubTab(key); if (key === 'duplicates') loadDuplicates(); }}
                                            style={{
                                                padding: '8px 18px', borderRadius: 8, border: `1.5px solid ${reportsSubTab === key ? T.indigo : T.border}`,
                                                background: reportsSubTab === key ? T.indigoLt : T.surface,
                                                color: reportsSubTab === key ? T.indigo : T.textSub,
                                                fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'all 0.18s ease',
                                            }}
                                        >{label}</button>
                                    ))}
                                </div>

                                {/* ── All Reports ── */}
                                {reportsSubTab === 'all' && (
                                    <LCard style={{ padding: 0, overflow: 'hidden' }}>
                                        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: T.bg }}>
                                            <span style={{ fontSize: 13, fontWeight: 700, color: T.text, display: 'flex', alignItems: 'center', gap: 7 }}>
                                                <FiFileText size={15} style={{ color: T.gold }} />{reportsTotal} numerology report{reportsTotal !== 1 ? 's' : ''}
                                            </span>
                                            <button onClick={loadReports} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8, border: `1.5px solid ${T.border}`, background: T.surface, color: T.textSub, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                                                <FiRefreshCw size={12} /> Reload
                                            </button>
                                        </div>
                                        {reportsLoading ? <SectionLoader /> : (
                                            <div style={{ overflowX: 'auto' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                    <thead>
                                                        <tr>{['#', 'Name', 'Mobile', 'DOB', 'Bhagyank', 'Mulank', 'Gender', 'Struggle', 'Date'].map(h => <th key={h} style={thSt}>{h}</th>)}</tr>
                                                    </thead>
                                                    <tbody>
                                                        {reports.map((r, i) => (
                                                            <React.Fragment key={r._id}>
                                                                <tr className="al-row" style={{ cursor: 'pointer' }} onClick={() => setExpandedReport(expandedReport === r._id ? null : r._id)}>
                                                                    <td style={{ ...thTd, color: T.textMute, fontSize: 12 }}>{(reportsPage - 1) * 10 + i + 1}</td>
                                                                    <td style={thTd}>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                            <Avatar first={r.firstName} last={r.lastName} size={30} />
                                                                            <span style={{ color: T.text, fontWeight: 700, fontSize: 14 }}>{r.firstName} {r.lastName}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td style={thTd}>{r.mobileNumber}</td>
                                                                    <td style={{ ...thTd, fontSize: 12 }}>{new Date(r.dob).toLocaleDateString('en-IN')}</td>
                                                                    <td style={thTd}><span style={{ background: '#fffbeb', color: '#92400e', fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 50, border: '1px solid #fde68a' }}>{r.bhagyank}</span></td>
                                                                    <td style={thTd}><span style={{ background: T.indigoLt, color: T.indigo, fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 50, border: `1px solid #c7d2fe` }}>{r.mulank ?? r.conductor ?? '—'}</span></td>
                                                                    <td style={{ ...thTd, textTransform: 'capitalize' }}>{r.gender}</td>
                                                                    <td style={thTd}>
                                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 180 }}>
                                                                            {(r.areaOfStruggle || []).slice(0, 2).map(a => <span key={a} style={{ fontSize: 10, background: T.bg, color: T.textSub, padding: '2px 7px', borderRadius: 4, border: `1px solid ${T.border}` }}>{a}</span>)}
                                                                            {(r.areaOfStruggle || []).length > 2 && <span style={{ fontSize: 10, color: T.indigo, fontWeight: 700 }}>+{r.areaOfStruggle.length - 2}</span>}
                                                                        </div>
                                                                    </td>
                                                                    <td style={{ ...thTd, fontSize: 12, color: T.textMute }}>{new Date(r.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                                                                </tr>
                                                                {expandedReport === r._id && (
                                                                    <tr className="al-expand">
                                                                        <td colSpan={10}>
                                                                            <div style={{ background: T.indigoLt, borderLeft: `3px solid ${T.indigo}`, padding: '18px 24px', animation: 'fadeUp 0.2s ease' }}>
                                                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(170px,1fr))', gap: 14 }}>
                                                                                    {[
                                                                                        { label: 'Lucky Numbers', items: r.luckyNumbers || [], bg: '#dcfce7', color: '#16a34a', border: '#bbf7d0' },
                                                                                        { label: 'Enemy Numbers', items: r.unluckyNumbers || [], bg: '#fee2e2', color: '#dc2626', border: '#fecaca' },
                                                                                        { label: 'Missing Numbers', items: r.missingNumbers || [], bg: '#fffbeb', color: '#92400e', border: '#fde68a' },
                                                                                    ].map(g => (
                                                                                        <div key={g.label}>
                                                                                            <p style={{ fontSize: 10, color: T.textMute, textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>{g.label}</p>
                                                                                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                                                                                {g.items.map(n => <span key={n} style={{ width: 28, height: 28, borderRadius: '50%', background: g.bg, color: g.color, border: `1.5px solid ${g.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 }}>{n}</span>)}
                                                                                            </div>
                                                                                        </div>
                                                                                    ))}
                                                                                    <div>
                                                                                        <p style={{ fontSize: 10, color: T.textMute, textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>Lucky Colours</p>
                                                                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                                                                            {(r.luckyColours || []).map(c => (
                                                                                                <span key={c} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: T.textSub }}>
                                                                                                    <span style={{ width: 12, height: 12, borderRadius: '50%', background: c.toLowerCase(), border: `1px solid ${T.border}`, display: 'inline-block' }} />{c}
                                                                                                </span>
                                                                                            ))}
                                                                                        </div>
                                                                                    </div>
                                                                                    <div>
                                                                                        <p style={{ fontSize: 10, color: T.textMute, textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>Email</p>
                                                                                        <p style={{ fontSize: 13, color: T.textSub }}>{r.email}</p>
                                                                                    </div>
                                                                                </div>
                                                                                {/* ── Delete Report Action ── */}
                                                                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
                                                                                    <button
                                                                                        onClick={() => handleDeleteReport(r._id, `${r.firstName} ${r.lastName}`)}
                                                                                        disabled={deletingReport === r._id}
                                                                                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', borderRadius: 8, border: '1.5px solid rgba(244,63,94,0.4)', background: 'rgba(244,63,94,0.07)', color: '#f43f5e', fontWeight: 700, fontSize: 12, cursor: deletingReport === r._id ? 'not-allowed' : 'pointer', opacity: deletingReport === r._id ? 0.6 : 1, transition: 'all 0.18s ease' }}
                                                                                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(244,63,94,0.15)'; e.currentTarget.style.borderColor = '#f43f5e'; }}
                                                                                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(244,63,94,0.07)'; e.currentTarget.style.borderColor = 'rgba(244,63,94,0.4)'; }}
                                                                                    >
                                                                                        <FiTrash2 size={13} /> {deletingReport === r._id ? 'Deleting…' : 'Delete Report'}
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </React.Fragment>
                                                        ))}
                                                        {reports.length === 0 && <tr><td colSpan={10} style={{ textAlign: 'center', padding: 48, color: T.textMute }}><FiFileText size={32} style={{ display: 'block', margin: '0 auto 8px', opacity: 0.4 }} />No reports found</td></tr>}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                        <Pagination page={reportsPage} pages={reportsPages} onPage={p => setReportsPage(p)} />
                                    </LCard>
                                )}

                                {/* ── Duplicates Sub-tab ── */}
                                {reportsSubTab === 'duplicates' && (
                                    <LCard style={{ padding: 0, overflow: 'hidden' }}>
                                        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: T.bg }}>
                                            <span style={{ fontSize: 13, fontWeight: 700, color: T.text, display: 'flex', alignItems: 'center', gap: 7 }}>
                                                ⚠️ {duplicates.length} duplicate group{duplicates.length !== 1 ? 's' : ''}
                                                {duplicates.length > 0 && (
                                                    <span style={{ background: '#fee2e2', color: '#dc2626', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 50, marginLeft: 4 }}>
                                                        {duplicates.reduce((s, g) => s + g.duplicateIds.length, 0)} to remove
                                                    </span>
                                                )}
                                            </span>
                                            <div style={{ display: 'flex', gap: 8 }}>
                                                <button onClick={loadDuplicates} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8, border: `1.5px solid ${T.border}`, background: T.surface, color: T.textSub, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                                                    <FiRefreshCw size={12} /> Reload
                                                </button>
                                                {duplicates.length > 0 && (
                                                    <button
                                                        onClick={handleDeleteAllDuplicates}
                                                        disabled={deletingDuplicates}
                                                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', borderRadius: 8, border: '1.5px solid rgba(244,63,94,0.5)', background: 'rgba(244,63,94,0.08)', color: '#f43f5e', fontWeight: 700, fontSize: 12, cursor: deletingDuplicates ? 'not-allowed' : 'pointer', opacity: deletingDuplicates ? 0.6 : 1 }}
                                                    >
                                                        <FiTrash2 size={12} /> {deletingDuplicates ? 'Deleting…' : 'Delete All Duplicates'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {duplicatesLoading ? <SectionLoader /> : duplicates.length === 0 ? (
                                            <div style={{ textAlign: 'center', padding: 48, color: T.textMute }}>
                                                <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                                                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>No Duplicates Found</div>
                                                <div style={{ fontSize: 13 }}>All reports are unique. Nothing to clean up.</div>
                                            </div>
                                        ) : (
                                            <div style={{ padding: '0 0 8px' }}>
                                                {duplicates.map((group, gi) => (
                                                    <div key={gi} style={{ borderBottom: `1px solid ${T.border}`, padding: '16px 20px' }}>
                                                        {/* Group header */}
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                                                            <span style={{ background: '#fee2e2', color: '#dc2626', fontWeight: 800, fontSize: 12, padding: '3px 10px', borderRadius: 50, border: '1px solid #fecaca' }}>
                                                                {group.count} copies
                                                            </span>
                                                            <span style={{ fontWeight: 700, fontSize: 14, color: T.text }}>
                                                                {group.docs[0]?.firstName} {group.docs[0]?.lastName}
                                                            </span>
                                                            <span style={{ fontSize: 12, color: T.textMute }}>
                                                                DOB: {group.docs[0]?.dob ? new Date(group.docs[0].dob).toLocaleDateString('en-IN') : '—'}
                                                            </span>
                                                            <span style={{ fontSize: 12, color: T.textMute }}>·</span>
                                                            <span style={{ fontSize: 12, color: T.textMute }}>📱 {group.docs[0]?.mobileNumber}</span>
                                                            {group.user && (
                                                                <span style={{ fontSize: 11, color: T.indigo, background: T.indigoLt, padding: '2px 8px', borderRadius: 50, marginLeft: 4 }}>
                                                                    👤 {group.user.firstName} {group.user.lastName}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {/* Individual report rows */}
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                            {group.docs.map((doc, di) => (
                                                                <div key={doc._id} style={{
                                                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                                    padding: '10px 14px', borderRadius: 10,
                                                                    background: di === 0 ? '#f0fdf4' : '#fff1f2',
                                                                    border: `1px solid ${di === 0 ? '#bbf7d0' : '#fecaca'}`,
                                                                }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                        <span style={{
                                                                            fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 50,
                                                                            background: di === 0 ? '#dcfce7' : '#fee2e2',
                                                                            color: di === 0 ? '#16a34a' : '#dc2626',
                                                                            border: `1px solid ${di === 0 ? '#86efac' : '#fca5a5'}`,
                                                                        }}>
                                                                            {di === 0 ? '✓ ORIGINAL' : `DUPLICATE ${di}`}
                                                                        </span>
                                                                        <span style={{ fontSize: 12, color: T.textSub }}>
                                                                            Created: {new Date(doc.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                                        </span>
                                                                        <span style={{ fontSize: 11, color: T.textMute, fontFamily: 'monospace' }}>{doc._id}</span>
                                                                    </div>
                                                                    {di !== 0 && (
                                                                        <button
                                                                            onClick={() => handleDeleteSingleDupReport(doc._id, `${doc.firstName} ${doc.lastName}`)}
                                                                            disabled={deletingDupReport === doc._id}
                                                                            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 7, border: '1.5px solid rgba(244,63,94,0.4)', background: 'rgba(244,63,94,0.07)', color: '#f43f5e', fontWeight: 700, fontSize: 11, cursor: deletingDupReport === doc._id ? 'not-allowed' : 'pointer', opacity: deletingDupReport === doc._id ? 0.6 : 1 }}
                                                                        >
                                                                            <FiTrash2 size={11} /> {deletingDupReport === doc._id ? 'Deleting…' : 'Delete'}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </LCard>
                                )}

                                {/* ── Delete By Date Sub-tab ── */}
                                {reportsSubTab === 'delete-by-date' && (
                                    <LCard style={{ maxWidth: 600, padding: 32 }}>
                                        <h3 style={{ fontSize: 18, fontWeight: 800, color: T.text, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                                            📅 Bulk Delete Reports by Date
                                        </h3>
                                        <p style={{ color: T.textSub, fontSize: 14, marginBottom: 20 }}>
                                            Select a custom date range to delete all customer reports generated within those dates.
                                        </p>

                                        <div style={{
                                            background: 'rgba(239,68,68,0.08)', border: '1.5px solid rgba(239,68,68,0.25)',
                                            borderRadius: 12, padding: '14px 18px', marginBottom: 24, color: '#f87171', fontSize: 13,
                                            display: 'flex', gap: 10, alignItems: 'flex-start'
                                        }}>
                                            <span style={{ fontSize: 16 }}>⚠️</span>
                                            <div>
                                                <strong style={{ display: 'block', marginBottom: 2 }}>High-risk Action</strong>
                                                This will permanently delete all customer reports generated within the specified range. This cannot be undone.
                                            </div>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
                                            <div>
                                                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: T.textMute, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Start Date</label>
                                                <input
                                                    type="date"
                                                    value={deleteStartDate}
                                                    onChange={e => setDeleteStartDate(e.target.value)}
                                                    style={{
                                                        width: '100%', padding: '11px 14px', borderRadius: 10,
                                                        border: `1.5px solid ${T.border}`, background: T.surface, color: T.text,
                                                        fontSize: 14, outline: 'none'
                                                    }}
                                                />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: T.textMute, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>End Date</label>
                                                <input
                                                    type="date"
                                                    value={deleteEndDate}
                                                    onChange={e => setDeleteEndDate(e.target.value)}
                                                    style={{
                                                        width: '100%', padding: '11px 14px', borderRadius: 10,
                                                        border: `1.5px solid ${T.border}`, background: T.surface, color: T.text,
                                                        fontSize: 14, outline: 'none'
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        <button
                                            onClick={handleDeleteReportsByDate}
                                            disabled={deletingByDate}
                                            style={{
                                                width: '100%', padding: '14px 20px', borderRadius: 50, border: 'none',
                                                background: deletingByDate ? 'rgba(239,68,68,0.4)' : 'linear-gradient(135deg, #ef4444, #dc2626)',
                                                color: '#fff', fontSize: 14, fontWeight: 800, cursor: deletingByDate ? 'not-allowed' : 'pointer',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                                boxShadow: deletingByDate ? 'none' : '0 4px 16px rgba(239,68,68,0.3)',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            <FiTrash2 size={15} /> {deletingByDate ? 'Deleting Reports...' : 'Delete Selected Reports'}
                                        </button>
                                    </LCard>
                                )}
                            </div>
                        )}

                        {/* ══ PRICING ════════════════════════════════════════════════ */}
                        {tab === 'pricing' && (
                            <div>
                                <LCard style={{ maxWidth: 640 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
                                        <div style={{ width: 44, height: 44, borderRadius: 12, background: T.goldLt, border: `1.5px solid ${T.gold}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>💰</div>
                                        <div>
                                            <h2 style={{ fontSize: 20, fontWeight: 800, color: T.text }}>Tool Pricing</h2>
                                            <p style={{ fontSize: 13, color: T.textMute }}>Manage the sale price shown to users</p>
                                        </div>
                                    </div>

                                    {pricingLoading ? <SectionLoader /> : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                                            {/* Tool Name */}
                                            <div>
                                                <label style={{ fontSize: 12, fontWeight: 700, color: T.textMute, textTransform: 'uppercase', letterSpacing: 0.7, display: 'block', marginBottom: 8 }}>Tool Name</label>
                                                <input
                                                    style={{ width: '100%', padding: '12px 16px', background: T.bg, border: `1.5px solid ${T.border}`, borderRadius: 10, color: T.text, fontSize: 15, fontWeight: 700, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                                                    value={pricingForm.toolName || ''}
                                                    onChange={e => setPricingForm(f => ({ ...f, toolName: e.target.value }))}
                                                    onFocus={e => e.target.style.borderColor = T.indigo}
                                                    onBlur={e => e.target.style.borderColor = T.border}
                                                />
                                            </div>

                                            {/* Prices */}
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                                <div>
                                                    <label style={{ fontSize: 12, fontWeight: 700, color: T.textMute, textTransform: 'uppercase', letterSpacing: 0.7, display: 'block', marginBottom: 8 }}>
                                                        Sale Price (₹) <span style={{ color: T.emerald }}>← Current</span>
                                                    </label>
                                                    <div style={{ position: 'relative' }}>
                                                        <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: T.gold, fontWeight: 800, fontSize: 18 }}>₹</span>
                                                        <input
                                                            style={{ width: '100%', padding: '12px 16px 12px 32px', background: T.bg, border: `2px solid ${T.gold}40`, borderRadius: 10, color: T.text, fontSize: 20, fontWeight: 900, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                                                            type="number" min="0"
                                                            value={pricingForm.toolPrice || 0}
                                                            onChange={e => setPricingForm(f => ({ ...f, toolPrice: e.target.value }))}
                                                            onFocus={e => e.target.style.borderColor = T.gold}
                                                            onBlur={e => e.target.style.borderColor = `${T.gold}40`}
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label style={{ fontSize: 12, fontWeight: 700, color: T.textMute, textTransform: 'uppercase', letterSpacing: 0.7, display: 'block', marginBottom: 8 }}>
                                                        Original Price (₹) <span style={{ color: T.textMute }}>← Strikethrough</span>
                                                    </label>
                                                    <div style={{ position: 'relative' }}>
                                                        <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: T.textMute, fontWeight: 800, fontSize: 18 }}>₹</span>
                                                        <input
                                                            style={{ width: '100%', padding: '12px 16px 12px 32px', background: T.bg, border: `1.5px solid ${T.border}`, borderRadius: 10, color: T.text, fontSize: 20, fontWeight: 900, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', textDecoration: 'line-through', color: T.textMute }}
                                                            type="number" min="0"
                                                            value={pricingForm.toolOriginalPrice || 0}
                                                            onChange={e => setPricingForm(f => ({ ...f, toolOriginalPrice: e.target.value }))}
                                                            onFocus={e => { e.target.style.borderColor = T.indigo; e.target.style.textDecoration = 'none'; }}
                                                            onBlur={e => { e.target.style.borderColor = T.border; e.target.style.textDecoration = 'line-through'; }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Offer Label */}
                                            <div>
                                                <label style={{ fontSize: 12, fontWeight: 700, color: T.textMute, textTransform: 'uppercase', letterSpacing: 0.7, display: 'block', marginBottom: 8 }}>Offer Badge Label</label>
                                                <input
                                                    style={{ width: '100%', padding: '12px 16px', background: T.bg, border: `1.5px solid ${T.border}`, borderRadius: 10, color: T.text, fontSize: 14, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                                                    placeholder="e.g. 50% OFF — Limited Time"
                                                    value={pricingForm.offerLabel || ''}
                                                    onChange={e => setPricingForm(f => ({ ...f, offerLabel: e.target.value }))}
                                                    onFocus={e => e.target.style.borderColor = T.indigo}
                                                    onBlur={e => e.target.style.borderColor = T.border}
                                                />
                                            </div>

                                            {/* Preview */}
                                            <div style={{ padding: '16px 20px', background: T.goldLt, border: `1.5px solid ${T.gold}30`, borderRadius: 14, marginBottom: 24 }}>
                                                <p style={{ fontSize: 11, fontWeight: 700, color: T.textMute, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>Live Preview</p>
                                                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                                                    <span style={{ fontSize: 36, fontWeight: 900, color: T.gold }}>₹{Number(pricingForm.toolPrice || 0).toLocaleString('en-IN')}</span>
                                                    <span style={{ fontSize: 18, color: T.textMute, textDecoration: 'line-through', fontWeight: 600 }}>₹{Number(pricingForm.toolOriginalPrice || 0).toLocaleString('en-IN')}</span>
                                                    <span style={{ fontSize: 12, fontWeight: 800, color: '#dc2626', background: '#fee2e2', padding: '3px 10px', borderRadius: 50 }}>
                                                        {pricingForm.offerLabel}
                                                    </span>
                                                </div>
                                                <p style={{ fontSize: 12, color: T.textMute, marginTop: 6 }}>
                                                    You save: ₹{(Number(pricingForm.toolOriginalPrice || 0) - Number(pricingForm.toolPrice || 0)).toLocaleString('en-IN')}
                                                </p>
                                            </div>

                                            {/* Save Button */}
                                            <button onClick={handleSavePricing} disabled={pricingSaving} style={{
                                                padding: '14px 24px', borderRadius: 12, border: 'none',
                                                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                                color: '#1a0a00', fontSize: 15, fontWeight: 800, cursor: pricingSaving ? 'not-allowed' : 'pointer',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                                                boxShadow: '0 6px 24px rgba(245,158,11,0.35)',
                                                transition: 'all 0.2s',
                                                opacity: pricingSaving ? 0.7 : 1,
                                            }}>
                                                <FiSave size={16} /> {pricingSaving ? 'Saving…' : '💾 Save Pricing'}
                                            </button>
                                        </div>
                                    )}
                                </LCard>
                            </div>
                        )}

                        {/* ══ COUPONS ════════════════════════════════════════════════ */}
                        {tab === 'coupons' && (
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
                                    <div>
                                        <h2 style={{ fontSize: 20, fontWeight: 800, color: T.text }}>🎟 Coupon Codes</h2>
                                        <p style={{ fontSize: 13, color: T.textMute }}>Create and manage discount coupons for users</p>
                                    </div>
                                    <button onClick={() => setCouponModal({})} style={{
                                        display: 'flex', alignItems: 'center', gap: 8, padding: '11px 20px',
                                        borderRadius: 10, border: 'none',
                                        background: 'linear-gradient(135deg, #4f6ef7, #3451d1)',
                                        color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                                        boxShadow: '0 4px 16px rgba(79,110,247,0.3)',
                                    }}>
                                        <FiPlus size={15} /> Create Coupon
                                    </button>
                                </div>

                                <LCard style={{ padding: 0, overflow: 'hidden' }}>
                                    {couponsLoading ? <SectionLoader /> : (
                                        <>
                                            {coupons.length === 0 ? (
                                                <div style={{ textAlign: 'center', padding: '60px 24px', color: T.textMute }}>
                                                    <div style={{ fontSize: 40, marginBottom: 12 }}>🎟</div>
                                                    <p style={{ fontSize: 15, fontWeight: 600 }}>No coupons yet</p>
                                                    <p style={{ fontSize: 13, marginTop: 4 }}>Click "Create Coupon" to add your first discount code.</p>
                                                </div>
                                            ) : (
                                                <div style={{ overflowX: 'auto' }}>
                                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                        <thead>
                                                            <tr>{['Code', 'Type', 'Discount', 'Min Order', 'Uses', 'Expires', 'Status', 'Actions'].map(h => <th key={h} style={thSt}>{h}</th>)}</tr>
                                                        </thead>
                                                        <tbody>
                                                            {coupons.map(c => (
                                                                <tr key={c._id} className="al-row">
                                                                    <td style={thTd}>
                                                                        <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 14, color: T.text, letterSpacing: 1 }}>{c.code}</span>
                                                                    </td>
                                                                    <td style={thTd}>
                                                                        <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 50, background: c.type === 'percent' ? T.indigoLt : T.goldLt, color: c.type === 'percent' ? T.indigo : T.gold }}>
                                                                            {c.type === 'percent' ? '% Percent' : '₹ Flat'}
                                                                        </span>
                                                                    </td>
                                                                    <td style={thTd}>
                                                                        <span style={{ fontSize: 16, fontWeight: 900, color: T.emerald }}>
                                                                            {c.type === 'percent' ? `${c.value}%` : `₹${c.value}`}
                                                                        </span>
                                                                    </td>
                                                                    <td style={thTd}>{c.minOrderAmount > 0 ? `₹${c.minOrderAmount}` : <span style={{ color: T.textMute }}>—</span>}</td>
                                                                    <td style={thTd}>
                                                                        <span style={{ fontSize: 13, color: T.text }}>
                                                                            {c.usedCount}
                                                                            {c.maxUses ? <span style={{ color: T.textMute }}>/{c.maxUses}</span> : <span style={{ color: T.textMute }}>  ∞</span>}
                                                                        </span>
                                                                    </td>
                                                                    <td style={{ ...thTd, fontSize: 12, color: T.textMute }}>
                                                                        {c.expiresAt ? new Date(c.expiresAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : <span>No expiry</span>}
                                                                    </td>
                                                                    <td style={thTd}>
                                                                        <button onClick={() => handleToggleCoupon(c)} style={{
                                                                            display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 50,
                                                                            fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer',
                                                                            background: c.isActive ? '#dcfce7' : '#fee2e2',
                                                                            color: c.isActive ? '#16a34a' : '#dc2626',
                                                                        }}>
                                                                            {c.isActive ? <><FiCheckCircle size={11} /> Active</> : <><FiAlertCircle size={11} /> Inactive</>}
                                                                        </button>
                                                                    </td>
                                                                    <td style={thTd}>
                                                                        <div style={{ display: 'flex', gap: 8 }}>
                                                                            <button onClick={() => setCouponModal(c)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 8, border: `1.5px solid ${T.border}`, background: T.surface, color: T.indigo, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                                                                                <FiEdit2 size={11} /> Edit
                                                                            </button>
                                                                            <button disabled={deletingCoupon === c._id} onClick={() => handleDeleteCoupon(c._id)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 8, border: '1.5px solid #fecaca', background: '#fff', color: '#dc2626', fontWeight: 600, fontSize: 12, cursor: 'pointer', opacity: deletingCoupon === c._id ? 0.5 : 1 }}>
                                                                                <FiTrash2 size={11} /> Delete
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </LCard>
                            </div>
                        )}

                        {/* ══ BRACELETS ══════════════════════════════════════════════ */}
                        {tab === 'bracelets' && (
                            <div>
                                {/* Bracelet Modal */}
                                {braceletModal !== null && (
                                    <BraceletModal
                                        product={braceletModal._id ? braceletModal : null}
                                        initialNumber={braceletModal.number}
                                        onClose={() => setBraceletModal(null)}
                                        onSave={handleSaveBracelet}
                                        T={T}
                                    />
                                )}

                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
                                    <div>
                                        <h2 style={{ fontSize: 20, fontWeight: 800, color: T.text }}>💎 Crystal Products</h2>
                                        <p style={{ fontSize: 13, color: T.textMute }}>Assign a crystal product to each missing number (1–9) — shown in "Crystals Missing in your Chart" section</p>
                                    </div>
                                    <button onClick={loadBracelets} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.surface, color: T.textSub, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                                        <FiRefreshCw size={13} /> Refresh
                                    </button>
                                </div>

                                {braceletsLoading ? <SectionLoader /> : (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
                                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => {
                                            const product = bracelets.find(b => b.number === num);
                                            const color = BRACELET_COLORS[num];
                                            return (
                                                <div key={num} style={{
                                                    background: T.surface, border: `1.5px solid ${color}30`,
                                                    borderRadius: 16, padding: 20, position: 'relative',
                                                    boxShadow: `0 2px 16px ${color}12`,
                                                    transition: 'all 0.2s ease',
                                                }}
                                                    onMouseEnter={e => { e.currentTarget.style.borderColor = `${color}70`; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                                                    onMouseLeave={e => { e.currentTarget.style.borderColor = `${color}30`; e.currentTarget.style.transform = 'translateY(0)'; }}
                                                >
                                                    {/* Number badge */}
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                                                        <div style={{
                                                            width: 44, height: 44, borderRadius: '50%',
                                                            background: `${color}20`, border: `2px solid ${color}`,
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            fontSize: 20, fontWeight: 900, color,
                                                        }}>{num}</div>
                                                        {product && (
                                                            <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 50, background: product.isActive ? '#dcfce7' : '#fee2e2', color: product.isActive ? '#16a34a' : '#dc2626' }}>
                                                                {product.isActive ? '● Active' : '● Inactive'}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {product ? (
                                                        <>
                                                            {product.imageUrl && (
                                                                <img src={product.imageUrl} alt={product.name}
                                                                    style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 10, marginBottom: 10, border: `1px solid ${color}20` }}
                                                                    onError={e => { e.target.style.display = 'none'; }}
                                                                />
                                                            )}
                                                            <div style={{ fontWeight: 700, fontSize: 14, color: T.text, marginBottom: 4 }}>{product.name}</div>
                                                            {product.description && <div style={{ fontSize: 12, color: T.textMute, marginBottom: 8, lineHeight: 1.4 }}>{product.description}</div>}
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                                                <span style={{ fontSize: 18, fontWeight: 900, color }}>
                                                                    ₹{product.price}
                                                                </span>
                                                                {product.originalPrice && (
                                                                    <span style={{ fontSize: 13, color: T.textMute, textDecoration: 'line-through' }}>₹{product.originalPrice}</span>
                                                                )}
                                                            </div>
                                                            <div style={{ display: 'flex', gap: 8 }}>
                                                                <button onClick={() => setBraceletModal(product)} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '9px', borderRadius: 8, border: `1.5px solid ${T.border}`, background: T.bg, color: T.indigo, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                                                                    <FiEdit2 size={12} /> Edit
                                                                </button>
                                                                <button disabled={deletingBracelet === product._id} onClick={() => handleDeleteBracelet(product._id)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '9px 14px', borderRadius: 8, border: '1.5px solid #fecaca', background: '#fff5f5', color: '#dc2626', fontWeight: 600, fontSize: 12, cursor: 'pointer', opacity: deletingBracelet === product._id ? 0.5 : 1 }}>
                                                                    <FiTrash2 size={12} />
                                                                </button>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div style={{ fontSize: 13, color: T.textMute, marginBottom: 14, fontStyle: 'italic' }}>No product assigned yet</div>
                                                            <button
                                                                onClick={() => setBraceletModal({ number: num })}
                                                                style={{
                                                                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                                                                    padding: '10px', borderRadius: 10, border: `1.5px dashed ${color}60`,
                                                                    background: `${color}08`, color, fontWeight: 700, fontSize: 13, cursor: 'pointer',
                                                                    transition: 'all 0.2s',
                                                                }}
                                                                onMouseEnter={e => { e.currentTarget.style.background = `${color}18`; }}
                                                                onMouseLeave={e => { e.currentTarget.style.background = `${color}08`; }}
                                                            >
                                                                <FiPlus size={14} /> Add Product
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ══ COMMON PRODUCTS ════════════════════════════════════════ */}
                        {tab === 'products' && (
                            <div>
                                {/* Common Product Modal */}
                                {commonProductModal !== null && (
                                    <CommonProductModal
                                        product={commonProductModal._id ? commonProductModal : null}
                                        onClose={() => setCommonProductModal(null)}
                                        onSave={handleSaveCommonProduct}
                                        T={T}
                                    />
                                )}

                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
                                    <div>
                                        <h2 style={{ fontSize: 20, fontWeight: 800, color: T.text }}>🛍️ Common Products</h2>
                                        <p style={{ fontSize: 13, color: T.textMute }}>Products shown universally to all users (e.g. Selenite Plate, generic items)</p>
                                    </div>
                                    <div style={{ display: 'flex', gap: 10 }}>
                                        <button onClick={loadCommonProducts} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.surface, color: T.textSub, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                                            <FiRefreshCw size={13} /> Refresh
                                        </button>
                                        <button onClick={() => setCommonProductModal({})} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', boxShadow: '0 4px 14px rgba(16,185,129,0.3)' }}>
                                            <FiPlus size={14} /> Add Product
                                        </button>
                                    </div>
                                </div>

                                {commonProductsLoading ? <SectionLoader /> : (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
                                        {commonProducts.length === 0 ? (
                                            <div style={{ gridColumn: '1 / -1', padding: 40, textAlign: 'center', color: T.textMute, background: T.surface, borderRadius: 16, border: `1px dashed ${T.border}` }}>
                                                No common products created yet.
                                            </div>
                                        ) : (
                                            commonProducts.map(product => (
                                                <div key={product._id} style={{
                                                    background: T.surface, border: `1px solid ${T.border}`,
                                                    borderRadius: 16, padding: 20, position: 'relative',
                                                    boxShadow: `0 2px 16px rgba(0,0,0,0.02)`,
                                                }}>
                                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                                                        <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 50, background: product.isActive ? '#dcfce7' : '#fee2e2', color: product.isActive ? '#16a34a' : '#dc2626' }}>
                                                            {product.isActive ? '● Active' : '● Inactive'}
                                                        </span>
                                                    </div>

                                                    {product.imageUrl && (
                                                        <img src={product.imageUrl} alt={product.name}
                                                            style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 10, marginBottom: 10, border: `1px solid ${T.border}` }}
                                                            onError={e => { e.target.style.display = 'none'; }}
                                                        />
                                                    )}
                                                    <div style={{ fontWeight: 700, fontSize: 14, color: T.text, marginBottom: 4 }}>{product.name}</div>
                                                    {product.description && <div style={{ fontSize: 12, color: T.textMute, marginBottom: 8, lineHeight: 1.4 }}>{product.description}</div>}
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                                        <span style={{ fontSize: 18, fontWeight: 900, color: T.gold }}>₹{product.price}</span>
                                                        {product.originalPrice && <span style={{ fontSize: 13, color: T.textMute, textDecoration: 'line-through' }}>₹{product.originalPrice}</span>}
                                                    </div>
                                                    <div style={{ display: 'flex', gap: 8 }}>
                                                        <button onClick={() => setCommonProductModal(product)} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '9px', borderRadius: 8, border: `1.5px solid ${T.border}`, background: T.bg, color: T.indigo, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                                                            <FiEdit2 size={12} /> Edit
                                                        </button>
                                                        <button disabled={deletingCommonProduct === product._id} onClick={() => handleDeleteCommonProduct(product._id)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '9px 14px', borderRadius: 8, border: '1.5px solid #fecaca', background: '#fff5f5', color: '#dc2626', fontWeight: 600, fontSize: 12, cursor: 'pointer', opacity: deletingCommonProduct === product._id ? 0.5 : 1 }}>
                                                            <FiTrash2 size={12} />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ══ WALLPAPERS ══════════════════════════════════════════════ */}
                        {tab === 'wallpapers' && (
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
                                    <div>
                                        <h2 style={{ fontSize: 20, fontWeight: 800, color: T.text }}>🖼️ Lucky Wallpapers</h2>
                                        <p style={{ fontSize: 13, color: T.textMute }}>Add wallpaper images for each Mulank (1–9). Users see these based on their birth date.</p>
                                    </div>
                                    <button onClick={loadWallpapers} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.surface, color: T.textSub, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                                        <FiRefreshCw size={13} /> Refresh
                                    </button>
                                </div>

                                {/* ── Add Image Form ── */}
                                <LCard style={{ marginBottom: 24 }}>
                                    <h3 style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <FiPlus size={15} style={{ color: T.indigo }} /> Add New Wallpaper Image
                                    </h3>
                                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 200px auto', gap: 12, alignItems: 'flex-end' }}>
                                        {/* Number selector */}
                                        <div>
                                            <label style={{ fontSize: 11, fontWeight: 700, color: T.textMute, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.7 }}>Mulank</label>
                                            <select
                                                value={wpForm.number}
                                                onChange={e => setWpForm(f => ({ ...f, number: parseInt(e.target.value) }))}
                                                style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 14, outline: 'none', cursor: 'pointer', fontWeight: 700 }}
                                            >
                                                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
                                                    <option key={n} value={n}>{WP_EMOJI[n]} Mulank {n}</option>
                                                ))}
                                            </select>
                                        </div>
                                        {/* Image URL */}
                                        <div>
                                            <label style={{ fontSize: 11, fontWeight: 700, color: T.textMute, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.7 }}>Image URL</label>
                                            <input
                                                value={wpForm.url}
                                                onChange={e => setWpForm(f => ({ ...f, url: e.target.value }))}
                                                placeholder="https://example.com/wallpaper.jpg"
                                                style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                                            />
                                        </div>
                                        {/* Label */}
                                        <div>
                                            <label style={{ fontSize: 11, fontWeight: 700, color: T.textMute, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.7 }}>Label (optional)</label>
                                            <input
                                                value={wpForm.label}
                                                onChange={e => setWpForm(f => ({ ...f, label: e.target.value }))}
                                                placeholder="e.g. Style 1"
                                                style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                                            />
                                        </div>
                                        {/* Submit */}
                                        <button
                                            onClick={handleAddWallpaperImage}
                                            disabled={wpSaving}
                                            style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: `linear-gradient(135deg,${T.indigo},#3451d1)`, color: '#fff', fontWeight: 700, fontSize: 13, cursor: wpSaving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap', boxShadow: '0 4px 16px rgba(79,110,247,0.3)', opacity: wpSaving ? 0.7 : 1 }}
                                        >
                                            <FiPlus size={14} /> {wpSaving ? 'Adding…' : 'Add Image'}
                                        </button>
                                    </div>
                                    {/* Image preview */}
                                    {wpForm.url && (
                                        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 12, background: T.bg, border: `1px solid ${T.border}` }}>
                                            <img src={wpForm.url} alt="preview" style={{ width: 48, height: 72, objectFit: 'cover', borderRadius: 8, border: `1.5px solid ${T.border}` }} onError={e => { e.target.src = ''; e.target.style.display = 'none'; }} />
                                            <div style={{ fontSize: 12, color: T.textMute }}>Preview — make sure the URL is publicly accessible</div>
                                        </div>
                                    )}
                                </LCard>

                                {/* ── Number cards grid ── */}
                                {wallpapersLoading ? <SectionLoader /> : (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
                                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => {
                                            const wpDoc = wallpapers.find(w => w.number === num);
                                            const images = wpDoc?.images || [];
                                            const color = WP_COLORS[num];
                                            return (
                                                <div key={num} style={{ background: T.surface, border: `1.5px solid ${color}30`, borderRadius: 16, overflow: 'hidden', boxShadow: `0 2px 16px ${color}12`, transition: 'all 0.2s ease' }}
                                                    onMouseEnter={e => { e.currentTarget.style.borderColor = `${color}60`; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                                                    onMouseLeave={e => { e.currentTarget.style.borderColor = `${color}30`; e.currentTarget.style.transform = 'translateY(0)'; }}
                                                >
                                                    {/* Card header */}
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: `1px solid ${color}20`, background: `${color}08` }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                            <div style={{ width: 38, height: 38, borderRadius: '50%', background: `${color}20`, border: `2px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 900, color }}>{num}</div>
                                                            <div>
                                                                <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>{WP_EMOJI[num]} Mulank {num}</div>
                                                                <div style={{ fontSize: 11, color: T.textMute }}>{images.length} image{images.length !== 1 ? 's' : ''}</div>
                                                            </div>
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            {/* Active toggle */}
                                                            {wpDoc && (
                                                                <button
                                                                    onClick={() => handleToggleWallpaper(num)}
                                                                    style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 50, border: 'none', cursor: 'pointer', background: wpDoc.isActive ? '#dcfce7' : '#fee2e2', color: wpDoc.isActive ? '#16a34a' : '#dc2626' }}
                                                                >
                                                                    {wpDoc.isActive ? '● Active' : '● Off'}
                                                                </button>
                                                            )}
                                                            {/* Quick-add to this number */}
                                                            <button
                                                                onClick={() => setWpForm(f => ({ ...f, number: num }))}
                                                                style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, padding: '5px 10px', borderRadius: 8, border: `1.5px solid ${color}40`, background: 'transparent', color, cursor: 'pointer' }}
                                                            >
                                                                <FiPlus size={11} /> Add
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Images list */}
                                                    <div style={{ padding: 14 }}>
                                                        {images.length === 0 ? (
                                                            <div style={{ textAlign: 'center', padding: '24px 0', color: T.textMute, fontSize: 13 }}>
                                                                <div style={{ fontSize: 28, marginBottom: 8 }}>🖼️</div>
                                                                No wallpapers yet — click Add above
                                                            </div>
                                                        ) : (
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                                                                {images.map(img => (
                                                                    <div key={img._id} style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', aspectRatio: '9/16', background: T.bg, border: `1.5px solid ${T.border}` }}>
                                                                        <img
                                                                            src={img.url}
                                                                            alt={img.label}
                                                                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                                            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                                                        />
                                                                        {/* fallback */}
                                                                        <div style={{ display: 'none', position: 'absolute', inset: 0, alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 4, background: T.bg, color: T.textMute, fontSize: 10, textAlign: 'center', padding: 4 }}>
                                                                            ⚠️<br />Invalid URL
                                                                        </div>
                                                                        {/* Label */}
                                                                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent,rgba(0,0,0,0.7))', padding: '16px 6px 6px', fontSize: 9, fontWeight: 700, color: '#fff', textAlign: 'center' }}>
                                                                            {img.label || 'Style'}
                                                                        </div>
                                                                        {/* Delete */}
                                                                        <button
                                                                            onClick={() => handleDeleteWallpaperImage(num, img._id)}
                                                                            disabled={wpDeleting === img._id}
                                                                            style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: '50%', border: 'none', background: 'rgba(244,63,94,0.85)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, opacity: wpDeleting === img._id ? 0.5 : 1 }}
                                                                        >
                                                                            <FiX size={11} />
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ══ MOBILE COVERS ═══════════════════════════════════════════ */}
                        {tab === 'mobile-covers' && (
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
                                    <div>
                                        <h2 style={{ fontSize: 20, fontWeight: 800, color: T.text }}>📱 Mobile Covers</h2>
                                        <p style={{ fontSize: 13, color: T.textMute }}>Add mobile cover images for each Mulank (1–9). Users see these based on their birth number.</p>
                                    </div>
                                    <button onClick={loadMobileCovers} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.surface, color: T.textSub, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                                        <FiRefreshCw size={13} /> Refresh
                                    </button>
                                </div>

                                {/* ── Add Image Form ── */}
                                <LCard style={{ marginBottom: 24 }}>
                                    <h3 style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <FiPlus size={15} style={{ color: T.gold }} /> Add New Mobile Cover Image
                                    </h3>
                                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 200px auto', gap: 12, alignItems: 'flex-end' }}>
                                        {/* Number selector */}
                                        <div>
                                            <label style={{ fontSize: 11, fontWeight: 700, color: T.textMute, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.7 }}>Mulank</label>
                                            <select
                                                value={mcForm.number}
                                                onChange={e => setMcForm(f => ({ ...f, number: parseInt(e.target.value) }))}
                                                style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 14, outline: 'none', cursor: 'pointer', fontWeight: 700 }}
                                            >
                                                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
                                                    <option key={n} value={n}>{MC_EMOJI[n]} Mulank {n}</option>
                                                ))}
                                            </select>
                                        </div>
                                        {/* Image URL */}
                                        <div>
                                            <label style={{ fontSize: 11, fontWeight: 700, color: T.textMute, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.7 }}>Image URL</label>
                                            <input
                                                value={mcForm.url}
                                                onChange={e => setMcForm(f => ({ ...f, url: e.target.value }))}
                                                placeholder="https://example.com/mobile-cover.jpg"
                                                style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                                            />
                                        </div>
                                        {/* Label */}
                                        <div>
                                            <label style={{ fontSize: 11, fontWeight: 700, color: T.textMute, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.7 }}>Label (optional)</label>
                                            <input
                                                value={mcForm.label}
                                                onChange={e => setMcForm(f => ({ ...f, label: e.target.value }))}
                                                placeholder="e.g. Cover 1"
                                                style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                                            />
                                        </div>
                                        {/* Submit */}
                                        <button
                                            onClick={handleAddMobileCoverImage}
                                            disabled={mcSaving}
                                            style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: `linear-gradient(135deg,${T.gold},#d97706)`, color: '#fff', fontWeight: 700, fontSize: 13, cursor: mcSaving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap', boxShadow: '0 4px 16px rgba(245,158,11,0.3)', opacity: mcSaving ? 0.7 : 1 }}
                                        >
                                            <FiPlus size={14} /> {mcSaving ? 'Adding…' : 'Add Image'}
                                        </button>
                                    </div>
                                    {/* Image preview */}
                                    {mcForm.url && (
                                        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 12, background: T.bg, border: `1px solid ${T.border}` }}>
                                            <img src={mcForm.url} alt="preview" style={{ width: 48, height: 85, objectFit: 'cover', borderRadius: 8, border: `1.5px solid ${T.border}` }} onError={e => { e.target.src = ''; e.target.style.display = 'none'; }} />
                                            <div style={{ fontSize: 12, color: T.textMute }}>Preview — make sure the URL is publicly accessible</div>
                                        </div>
                                    )}
                                </LCard>

                                {/* ── Number cards grid ── */}
                                {mobileCoversLoading ? <SectionLoader /> : (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
                                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => {
                                            const mcDoc = mobileCovers.find(c => c.number === num);
                                            const images = mcDoc?.images || [];
                                            const color = MC_COLORS[num];
                                            return (
                                                <div key={num} style={{ background: T.surface, border: `1.5px solid ${color}30`, borderRadius: 16, overflow: 'hidden', boxShadow: `0 2px 16px ${color}12`, transition: 'all 0.2s ease' }}
                                                    onMouseEnter={e => { e.currentTarget.style.borderColor = `${color}60`; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                                                    onMouseLeave={e => { e.currentTarget.style.borderColor = `${color}30`; e.currentTarget.style.transform = 'translateY(0)'; }}
                                                >
                                                    {/* Card header */}
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: `1px solid ${color}20`, background: `${color}08` }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                            <div style={{ width: 38, height: 38, borderRadius: '50%', background: `${color}20`, border: `2px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 900, color }}>{num}</div>
                                                            <div>
                                                                <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>📱 Mulank {num}</div>
                                                                <div style={{ fontSize: 11, color: T.textMute }}>{images.length} cover{images.length !== 1 ? 's' : ''}</div>
                                                            </div>
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            {mcDoc && (
                                                                <button
                                                                    onClick={() => handleToggleMobileCover(num)}
                                                                    style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 50, border: 'none', cursor: 'pointer', background: mcDoc.isActive ? '#dcfce7' : '#fee2e2', color: mcDoc.isActive ? '#16a34a' : '#dc2626' }}
                                                                >
                                                                    {mcDoc.isActive ? '● Active' : '● Off'}
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={() => setMcForm(f => ({ ...f, number: num }))}
                                                                style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, padding: '5px 10px', borderRadius: 8, border: `1.5px solid ${color}40`, background: 'transparent', color, cursor: 'pointer' }}
                                                            >
                                                                <FiPlus size={11} /> Add
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Images list — portrait aspect ratio for phone covers */}
                                                    <div style={{ padding: 14 }}>
                                                        {images.length === 0 ? (
                                                            <div style={{ textAlign: 'center', padding: '24px 0', color: T.textMute, fontSize: 13 }}>
                                                                <div style={{ fontSize: 28, marginBottom: 8 }}>📱</div>
                                                                No covers yet — click Add above
                                                            </div>
                                                        ) : (
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                                                                {images.map(img => (
                                                                    <div key={img._id} style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', aspectRatio: '9/19.5', background: T.bg, border: `1.5px solid ${T.border}` }}>
                                                                        <img
                                                                            src={img.url}
                                                                            alt={img.label}
                                                                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                                            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                                                        />
                                                                        {/* fallback */}
                                                                        <div style={{ display: 'none', position: 'absolute', inset: 0, alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 4, background: T.bg, color: T.textMute, fontSize: 10, textAlign: 'center', padding: 4 }}>
                                                                            ⚠️<br />Invalid URL
                                                                        </div>
                                                                        {/* Label */}
                                                                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent,rgba(0,0,0,0.7))', padding: '16px 6px 6px', fontSize: 9, fontWeight: 700, color: '#fff', textAlign: 'center' }}>
                                                                            {img.label || 'Cover'}
                                                                        </div>
                                                                        {/* Delete */}
                                                                        <button
                                                                            onClick={() => handleDeleteMobileCoverImage(num, img._id)}
                                                                            disabled={mcDeleting === img._id}
                                                                            style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: '50%', border: 'none', background: 'rgba(244,63,94,0.85)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, opacity: mcDeleting === img._id ? 0.5 : 1 }}
                                                                        >
                                                                            <FiX size={11} />
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ══ YANTRAS ══════════════════════════════════════════════════ */}
                        {tab === 'yantras' && (
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
                                    <div>
                                        <h2 style={{ fontSize: 20, fontWeight: 800, color: T.text }}>🔱 Yantra Images</h2>
                                        <p style={{ fontSize: 13, color: T.textMute }}>Add yantra images for each Mulank (1–9). Users see these in their numerology report based on their birth number.</p>
                                    </div>
                                    <button onClick={loadYantras} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.surface, color: T.textSub, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                                        <FiRefreshCw size={13} /> Refresh
                                    </button>
                                </div>

                                {/* ── Add Image Form ── */}
                                <LCard style={{ marginBottom: 24 }}>
                                    <h3 style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <FiPlus size={15} style={{ color: '#f97316' }} /> Add New Yantra Image
                                    </h3>
                                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 200px auto', gap: 12, alignItems: 'flex-end' }}>
                                        <div>
                                            <label style={{ fontSize: 11, fontWeight: 700, color: T.textMute, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.7 }}>Mulank</label>
                                            <select
                                                value={yantraForm.number}
                                                onChange={e => setYantraForm(f => ({ ...f, number: parseInt(e.target.value) }))}
                                                style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 14, outline: 'none', cursor: 'pointer', fontWeight: 700 }}
                                            >
                                                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
                                                    <option key={n} value={n}>{YANTRA_EMOJI[n]} Mulank {n}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 11, fontWeight: 700, color: T.textMute, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.7 }}>Image URL</label>
                                            <input
                                                value={yantraForm.url}
                                                onChange={e => setYantraForm(f => ({ ...f, url: e.target.value }))}
                                                placeholder="https://example.com/yantra.jpg"
                                                style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                                            />
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 11, fontWeight: 700, color: T.textMute, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.7 }}>Label (optional)</label>
                                            <input
                                                value={yantraForm.label}
                                                onChange={e => setYantraForm(f => ({ ...f, label: e.target.value }))}
                                                placeholder="e.g. Surya Yantra"
                                                style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                                            />
                                        </div>
                                        <button
                                            onClick={handleAddYantraImage}
                                            disabled={yantraSaving}
                                            style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#f97316,#ea580c)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: yantraSaving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap', boxShadow: '0 4px 16px rgba(249,115,22,0.3)', opacity: yantraSaving ? 0.7 : 1 }}
                                        >
                                            <FiPlus size={14} /> {yantraSaving ? 'Adding…' : 'Add Image'}
                                        </button>
                                    </div>
                                    {yantraForm.url && (
                                        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 12, background: T.bg, border: `1px solid ${T.border}` }}>
                                            <img src={yantraForm.url} alt="preview" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 10, border: `1.5px solid ${T.border}` }} onError={e => { e.target.src = ''; e.target.style.display = 'none'; }} />
                                            <div style={{ fontSize: 12, color: T.textMute }}>Preview — make sure the URL is publicly accessible</div>
                                        </div>
                                    )}
                                </LCard>

                                {/* ── Number cards grid ── */}
                                {yantrasLoading ? <SectionLoader /> : (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
                                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => {
                                            const yantraDoc = yantras.find(y => y.number === num);
                                            const images = yantraDoc?.images || [];
                                            const color = YANTRA_COLORS[num];
                                            return (
                                                <div key={num} style={{ background: T.surface, border: `1.5px solid ${color}30`, borderRadius: 16, overflow: 'hidden', boxShadow: `0 2px 16px ${color}12`, transition: 'all 0.2s ease' }}
                                                    onMouseEnter={e => { e.currentTarget.style.borderColor = `${color}60`; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                                                    onMouseLeave={e => { e.currentTarget.style.borderColor = `${color}30`; e.currentTarget.style.transform = 'translateY(0)'; }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: `1px solid ${color}20`, background: `${color}08` }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                            <div style={{ width: 38, height: 38, borderRadius: '50%', background: `${color}20`, border: `2px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 900, color }}>{num}</div>
                                                            <div>
                                                                <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>{YANTRA_EMOJI[num]} Mulank {num}</div>
                                                                <div style={{ fontSize: 11, color: T.textMute }}>{images.length} image{images.length !== 1 ? 's' : ''}</div>
                                                            </div>
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            {yantraDoc && (
                                                                <button
                                                                    onClick={() => handleToggleYantra(num)}
                                                                    style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 50, border: 'none', cursor: 'pointer', background: yantraDoc.isActive ? '#dcfce7' : '#fee2e2', color: yantraDoc.isActive ? '#16a34a' : '#dc2626' }}
                                                                >
                                                                    {yantraDoc.isActive ? '● Active' : '● Off'}
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={() => setYantraForm(f => ({ ...f, number: num }))}
                                                                style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, padding: '5px 10px', borderRadius: 8, border: `1.5px solid ${color}40`, background: 'transparent', color, cursor: 'pointer' }}
                                                            >
                                                                <FiPlus size={11} /> Add
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div style={{ padding: 14 }}>
                                                        {images.length === 0 ? (
                                                            <div style={{ textAlign: 'center', padding: '24px 0', color: T.textMute, fontSize: 13 }}>
                                                                <div style={{ fontSize: 28, marginBottom: 8 }}>🔱</div>
                                                                No yantra images yet — click Add above
                                                            </div>
                                                        ) : (
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                                                                {images.map(img => (
                                                                    <div key={img._id} style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', aspectRatio: '1/1', background: T.bg, border: `1.5px solid ${T.border}` }}>
                                                                        <img
                                                                            src={img.url}
                                                                            alt={img.label}
                                                                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                                            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                                                        />
                                                                        <div style={{ display: 'none', position: 'absolute', inset: 0, alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 4, background: T.bg, color: T.textMute, fontSize: 10, textAlign: 'center', padding: 4 }}>
                                                                            ⚠️<br />Invalid URL
                                                                        </div>
                                                                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent,rgba(0,0,0,0.75))', padding: '14px 6px 6px', fontSize: 9, fontWeight: 700, color: '#fff', textAlign: 'center' }}>
                                                                            {img.label || 'Yantra'}
                                                                        </div>
                                                                        <button
                                                                            onClick={() => handleDeleteYantraImage(num, img._id)}
                                                                            disabled={yantraDeleting === img._id}
                                                                            style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: '50%', border: 'none', background: 'rgba(244,63,94,0.85)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, opacity: yantraDeleting === img._id ? 0.5 : 1 }}
                                                                        >
                                                                            <FiX size={11} />
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ══ ORDERS ══════════════════════════════════════════════════ */}

                        {tab === 'orders' && (() => {
                            const STATUS_PILLS = {
                                pending: { label: '⏳ Pending', bg: '#fef3c7', color: '#d97706', border: '#fcd34d' },
                                paid: { label: '💳 Paid', bg: '#e0f2fe', color: '#0369a1', border: '#bae6fd' },
                                shipped: { label: '🚚 Shipped', bg: '#ffedd5', color: '#ea580c', border: '#fed7aa' },
                                delivered: { label: '✅ Delivered', bg: '#dcfce7', color: '#15803d', border: '#bbf7d0' },
                                cancelled: { label: '❌ Cancelled', bg: '#fee2e2', color: '#b91c1c', border: '#fca5a5' }
                            };

                            return (
                                <div>
                                    {/* Header */}
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
                                        <div>
                                            <h2 style={{ fontSize: 20, fontWeight: 800, color: T.text }}>🛒 Physical Product Orders</h2>
                                            <p style={{ fontSize: 13, color: T.textMute }}>Track customer orders, manage shipments, and update fulfillment statuses</p>
                                        </div>

                                        {/* Status Filter and Revenue */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                            {/* Revenue Indicator */}
                                            <div style={{ background: T.emeraldLt || '#d1fae5', padding: '6px 14px', borderRadius: 10, border: `1.5px solid ${T.emerald || '#10b981'}40` }}>
                                                <span style={{ fontSize: 10, color: T.emerald || '#10b981', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 2 }}>Total Revenue</span>
                                                <div style={{ fontSize: 18, fontWeight: 900, color: T.text }}>₹{ordersRevenue.toLocaleString('en-IN')}</div>
                                            </div>

                                            {/* Status Filter */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <span style={{ fontSize: 13, fontWeight: 700, color: T.textSub }}>Filter Status:</span>
                                            <select
                                                value={ordersFilter}
                                                onChange={e => { setOrdersFilter(e.target.value); setOrdersPage(1); }}
                                                style={{
                                                    padding: '8px 14px', borderRadius: 8, background: T.surface, color: T.text,
                                                    border: `1.5px solid ${T.border}`, outline: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13
                                                }}
                                            >
                                                <option value="all">🌐 All Orders</option>
                                                <option value="pending">⏳ Pending</option>
                                                <option value="paid">💳 Paid</option>
                                                <option value="shipped">🚚 Shipped</option>
                                                <option value="delivered">✅ Delivered</option>
                                                <option value="cancelled">❌ Cancelled</option>
                                            </select>
                                        </div>
                                        </div>
                                    </div>

                                    {ordersLoading ? (
                                        <div style={{ padding: '60px 0', textAlign: 'center', color: T.textSub }}><span style={{ display: 'inline-block', animation: 'shimmer 1.5s infinite' }}>⏳</span> Loading orders...</div>
                                    ) : orders.length === 0 ? (
                                        <div style={{ background: T.surface, borderRadius: 16, border: `1.5px dashed ${T.border}`, padding: '48px 24px', textAlign: 'center', color: T.textMute }}>
                                            <div style={{ fontSize: 32, marginBottom: 8 }}>🛒</div>
                                            <p style={{ fontWeight: 700, margin: 0 }}>No orders found.</p>
                                            <p style={{ fontSize: 13, margin: '4px 0 0' }}>Orders will appear here once customers purchase crystals or charging plates.</p>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                            {orders.map(order => {
                                                const pill = STATUS_PILLS[order.status] || STATUS_PILLS.pending;
                                                return (
                                                    <div key={order._id} style={{
                                                        background: T.surface, borderRadius: 16, border: `1.5px solid ${T.border}`,
                                                        padding: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.02)',
                                                        display: 'flex', flexDirection: 'column', gap: 14
                                                    }}>
                                                        {/* Top Row: ID, Date, Status */}
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, paddingBottom: 12, borderBottom: `1.5px solid ${T.bg}` }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                <span style={{ fontSize: 14, fontWeight: 800, color: T.text, fontFamily: 'monospace' }}>#{order._id.slice(-10).toUpperCase()}</span>
                                                                <span style={{ fontSize: 12, color: T.textMute }}>
                                                                    📅 {new Date(order.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                                </span>
                                                            </div>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                                <button
                                                                    onClick={() => setSelectedOrder(order)}
                                                                    style={{
                                                                        background: `${T.gold}15`, color: T.gold, border: `1.5px solid ${T.gold}30`,
                                                                        fontSize: 11.5, fontWeight: 800, padding: '5px 12px', borderRadius: 8, cursor: 'pointer',
                                                                        transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 4
                                                                    }}
                                                                    onMouseEnter={e => { e.currentTarget.style.background = `${T.gold}30`; }}
                                                                    onMouseLeave={e => { e.currentTarget.style.background = `${T.gold}15`; }}
                                                                >
                                                                    👁️ View Details
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteOrder(order._id)}
                                                                    style={{
                                                                        background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1.5px solid rgba(239,68,68,0.3)',
                                                                        fontSize: 11.5, fontWeight: 800, padding: '5px 12px', borderRadius: 8, cursor: 'pointer',
                                                                        transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 4
                                                                    }}
                                                                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.2)'; }}
                                                                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; }}
                                                                    title="Delete Order"
                                                                >
                                                                    🗑️ Delete
                                                                </button>
                                                                <span style={{
                                                                    background: pill.bg, color: pill.color, border: `1px solid ${pill.border}`,
                                                                    fontSize: 11.5, fontWeight: 800, padding: '3px 10px', borderRadius: 20, textTransform: 'uppercase'
                                                                }}>{pill.label}</span>
                                                            </div>
                                                        </div>

                                                        {/* Middle Grid: Items, Shipping, Actions */}
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: 20 }}>
                                                            {/* Items */}
                                                            <div>
                                                                <h4 style={{ fontSize: 11.5, fontWeight: 700, color: T.textMute, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Items Purchased</h4>
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                                    {order.items.map((item, idx) => (
                                                                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: T.textSub, borderBottom: idx < order.items.length - 1 ? `1px dashed ${T.border}` : 'none', paddingBottom: 6 }}>
                                                                            <span style={{ fontWeight: 600 }}>
                                                                                {item.name}
                                                                                {item.variant && <span style={{ color: T.gold || '#d97706', fontSize: 11, marginLeft: 6, fontWeight: 700 }}>({item.variant})</span>}
                                                                                <strong style={{ color: T.indigo, marginLeft: 4 }}>x{item.quantity}</strong>
                                                                            </span>
                                                                            <span style={{ fontWeight: 700, color: T.text }}>₹{item.price.toLocaleString('en-IN')}</span>
                                                                        </div>
                                                                    ))}
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: `1.5px solid ${T.bg}`, paddingTop: 8, fontSize: 14, fontWeight: 800 }}>
                                                                        <span style={{ color: T.text }}>Total Paid</span>
                                                                        <span style={{ color: T.gold }}>₹{order.finalAmount.toLocaleString('en-IN')}</span>
                                                                    </div>
                                                                    {order.couponCode && (
                                                                        <div style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>
                                                                            🎟 Coupon applied: {order.couponCode} (-₹{order.discountAmount})
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Shipping Address */}
                                                            <div>
                                                                <h4 style={{ fontSize: 11.5, fontWeight: 700, color: T.textMute, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Shipping details</h4>
                                                                {order.shippingAddress ? (
                                                                    <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.5 }}>
                                                                        <div style={{ fontWeight: 800, color: T.text }}>{order.shippingAddress.fullName}</div>
                                                                        <div>{order.shippingAddress.addressLine1}</div>
                                                                        {order.shippingAddress.addressLine2 && <div>{order.shippingAddress.addressLine2}</div>}
                                                                        <div>{order.shippingAddress.city}, {order.shippingAddress.state} - {order.shippingAddress.postalCode}</div>
                                                                        <div style={{ marginTop: 4, fontWeight: 700 }}>📞 {order.shippingAddress.phone}</div>
                                                                    </div>
                                                                ) : (
                                                                    <div style={{ fontSize: 12.5, color: T.textMute }}>No shipping details.</div>
                                                                )}
                                                            </div>

                                                            {/* Actions & Logistics */}
                                                            <div>
                                                                <h4 style={{ fontSize: 11.5, fontWeight: 700, color: T.textMute, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Fulfillment Actions</h4>
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                                    {order.status === 'pending' && (
                                                                        <button
                                                                            onClick={() => handleUpdateOrderStatus(order._id, 'paid')}
                                                                            style={{
                                                                                width: '100%', padding: '10px 14px', borderRadius: 8, border: 'none',
                                                                                background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff',
                                                                                fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'all 0.2s'
                                                                            }}
                                                                            onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                                                                            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                                                                        >
                                                                            💳 Mark as Paid
                                                                        </button>
                                                                    )}

                                                                    {order.status === 'paid' && (
                                                                        <button
                                                                            onClick={() => handleUpdateOrderStatus(order._id, 'shipped')}
                                                                            style={{
                                                                                width: '100%', padding: '10px 14px', borderRadius: 8, border: 'none',
                                                                                background: 'linear-gradient(135deg, #f97316, #ea580c)', color: '#fff',
                                                                                fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'all 0.2s'
                                                                            }}
                                                                            onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                                                                            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                                                                        >
                                                                            🚚 Mark as Shipped
                                                                        </button>
                                                                    )}

                                                                    {order.status === 'shipped' && (
                                                                        <button
                                                                            onClick={() => handleUpdateOrderStatus(order._id, 'delivered')}
                                                                            style={{
                                                                                width: '100%', padding: '10px 14px', borderRadius: 8, border: 'none',
                                                                                background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff',
                                                                                fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'all 0.2s'
                                                                            }}
                                                                            onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                                                                            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                                                                        >
                                                                            ✅ Mark as Delivered
                                                                        </button>
                                                                    )}

                                                                    {order.status === 'delivered' && (
                                                                        <div style={{
                                                                            padding: '10px', borderRadius: 8, background: T.emeraldLt,
                                                                            border: `1px solid ${T.emerald}20`, textAlign: 'center', fontSize: 12.5,
                                                                            color: T.emerald, fontWeight: 700
                                                                        }}>
                                                                            🎉 Order Completed & Delivered
                                                                        </div>
                                                                    )}

                                                                    {['pending', 'paid', 'shipped'].includes(order.status) && (
                                                                        <button
                                                                            onClick={() => { if (window.confirm('Cancel and refund this order?')) handleUpdateOrderStatus(order._id, 'cancelled'); }}
                                                                            style={{
                                                                                width: '100%', padding: '6px 12px', borderRadius: 8, border: 'none',
                                                                                background: 'transparent', color: T.rose, fontWeight: 600, fontSize: 12,
                                                                                cursor: 'pointer', textDecoration: 'underline'
                                                                            }}
                                                                        >
                                                                            Cancel Order
                                                                        </button>
                                                                    )}

                                                                    {order.status === 'cancelled' && (
                                                                        <div style={{
                                                                            padding: '10px', borderRadius: 8, background: T.roseLt,
                                                                            border: `1px solid ${T.rose}20`, textAlign: 'center', fontSize: 12.5,
                                                                            color: T.rose, fontWeight: 700
                                                                        }}>
                                                                            ❌ Order Cancelled
                                                                        </div>
                                                                    )}

                                                                    {/* Logs / Timestamps */}
                                                                    <div style={{ fontSize: 11, color: T.textMute, marginTop: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                                        {order.paidAt && <div>· Paid at: {new Date(order.paidAt).toLocaleDateString('en-IN')}</div>}
                                                                        {order.shippedAt && <div>· Shipped at: {new Date(order.shippedAt).toLocaleDateString('en-IN')}</div>}
                                                                        {order.deliveredAt && <div>· Delivered at: {new Date(order.deliveredAt).toLocaleDateString('en-IN')}</div>}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            {/* Pagination */}
                                            <Pagination page={ordersPage} pages={ordersPages} onPage={setOrdersPage} />
                                        </div>
                                    )}
                                </div>
                            );
                        })()}

                        {/* ══ LOGS ════════════════════════════════════════════════════ */}
                        {tab === 'logs' && (() => {
                            const TYPE_CONFIG = {
                                report: { label: 'Report', bg: 'rgba(79,110,247,0.12)', color: '#4f6ef7', border: 'rgba(79,110,247,0.3)' },
                                user: { label: 'Registration', bg: 'rgba(16,185,129,0.12)', color: '#10b981', border: 'rgba(16,185,129,0.3)' },
                            };
                            const relTime = (d) => {
                                const s = Math.floor((Date.now() - new Date(d)) / 1000);
                                if (s < 60) return `${s}s ago`;
                                if (s < 3600) return `${Math.floor(s / 60)}m ago`;
                                if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
                                return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                            };
                            return (
                                <div>
                                    {/* Header */}
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
                                        <div>
                                            <h2 style={{ fontSize: 20, fontWeight: 800, color: T.text }}>📜 Activity Logs</h2>
                                            <p style={{ fontSize: 13, color: T.textMute }}>Unified feed of report submissions and user registrations · {logsTotal} total events</p>
                                        </div>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <button onClick={loadLogs} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.surface, color: T.textSub, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                                                <FiRefreshCw size={13} style={{ animation: logsLoading ? 'spin 1s linear infinite' : 'none' }} /> Refresh
                                            </button>
                                            <button
                                                onClick={handleClearLogs}
                                                disabled={logsClearLoading}
                                                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 10, border: '1.5px solid #fecaca', background: '#fff5f5', color: '#dc2626', fontWeight: 700, fontSize: 13, cursor: logsClearLoading ? 'not-allowed' : 'pointer', opacity: logsClearLoading ? 0.6 : 1 }}>
                                                🗑️ {logsClearLoading ? 'Clearing…' : 'Clear Logs'}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Search + Filter bar */}
                                    <LCard style={{ marginBottom: 20 }}>
                                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                                            <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
                                                <FiSearch size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: T.textMute }} />
                                                <input
                                                    placeholder="Search by name or email…"
                                                    value={logsSearch}
                                                    onChange={e => { setLogsSearch(e.target.value); setLogsPage(1); }}
                                                    style={{ width: '100%', padding: '10px 14px 10px 36px', borderRadius: 10, border: `1.5px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                                                />
                                            </div>
                                            <div style={{ display: 'flex', gap: 8 }}>
                                                {[['all', '🌐 All'], ['report', '📋 Reports'], ['user', '👤 Users']].map(([k, lbl]) => (
                                                    <button key={k} onClick={() => { setLogsType(k); setLogsPage(1); }} style={{
                                                        padding: '8px 16px', borderRadius: 50, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700,
                                                        background: logsType === k ? T.indigo : T.bg,
                                                        color: logsType === k ? '#fff' : T.textSub,
                                                        boxShadow: logsType === k ? `0 4px 14px ${T.indigo}40` : 'none',
                                                        transition: 'all 0.18s ease',
                                                    }}>{lbl}</button>
                                                ))}
                                            </div>
                                        </div>
                                    </LCard>

                                    {/* Log feed */}
                                    <LCard style={{ padding: 0, overflow: 'hidden' }}>
                                        {logsLoading ? (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60, gap: 12 }}>
                                                <div style={{ width: 20, height: 20, borderRadius: '50%', border: `3px solid ${T.indigoLt}`, borderTopColor: T.indigo, animation: 'spin 0.8s linear infinite' }} />
                                                <span style={{ color: T.textMute, fontSize: 14 }}>Loading logs…</span>
                                            </div>
                                        ) : logs.length === 0 ? (
                                            <div style={{ textAlign: 'center', padding: '60px 24px', color: T.textMute }}>
                                                <div style={{ fontSize: 42, marginBottom: 12 }}>📭</div>
                                                <div style={{ fontSize: 15, fontWeight: 700, color: T.textSub, marginBottom: 6 }}>No activity found</div>
                                                <div style={{ fontSize: 13 }}>Try adjusting your search or filter.</div>
                                            </div>
                                        ) : (
                                            <div>
                                                {logs.map((log, idx) => {
                                                    const cfg = TYPE_CONFIG[log.type] || TYPE_CONFIG.report;
                                                    return (
                                                        <div key={`${log._id}-${idx}`} style={{
                                                            display: 'flex', alignItems: 'flex-start', gap: 14,
                                                            padding: '16px 24px',
                                                            borderBottom: idx < logs.length - 1 ? `1px solid ${T.border}` : 'none',
                                                            transition: 'background 0.15s ease',
                                                        }}
                                                            onMouseEnter={e => { if (e.currentTarget) e.currentTarget.style.background = T.bg; }}
                                                            onMouseLeave={e => { if (e.currentTarget) e.currentTarget.style.background = 'transparent'; }}
                                                        >
                                                            <div style={{ width: 40, height: 40, borderRadius: 12, background: cfg.bg, border: `1.5px solid ${cfg.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                                                                {log.icon}
                                                            </div>
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                                                                    <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 10px', borderRadius: 50, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, textTransform: 'uppercase', letterSpacing: 0.8 }}>{cfg.label}</span>
                                                                    <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{log.title}</span>
                                                                </div>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                                                                    <span style={{ fontSize: 13, fontWeight: 700, color: T.indigo }}>{log.actor}</span>
                                                                    <span style={{ fontSize: 12, color: T.textMute }}>·</span>
                                                                    <span style={{ fontSize: 12, color: T.textMute }}>{log.actorEmail}</span>
                                                                </div>
                                                                <div style={{ fontSize: 11.5, color: T.textMute, lineHeight: 1.6 }}>{log.meta}</div>
                                                            </div>
                                                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                                                <div style={{ fontSize: 12, fontWeight: 700, color: T.textSub, whiteSpace: 'nowrap' }}>{relTime(log.createdAt)}</div>
                                                                <div style={{ fontSize: 10, color: T.textMute, marginTop: 2, whiteSpace: 'nowrap' }}>
                                                                    {new Date(log.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        <Pagination page={logsPage} pages={logsPages} onPage={p => setLogsPage(p)} />
                                    </LCard>
                                </div>
                            );
                        })()}

                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Common Product Modal ──────────────────────────────────────────────────────
function CommonProductModal({ product, onClose, onSave, T }) {
    const [form, setForm] = useState({
        name: '', description: '', price: '', originalPrice: '', imageUrl: '', buyLink: '', isActive: true, order: 0,
        variants: []
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (product) {
            setForm({
                name: product.name || '', description: product.description || '',
                price: product.price || '', originalPrice: product.originalPrice || '',
                imageUrl: product.imageUrl || '', buyLink: product.buyLink || '',
                isActive: product.isActive !== false, order: product.order || 0,
                variants: product.variants ? product.variants.map(v => ({ ...v })) : []
            });
        }
    }, [product]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.price) return toast.error('Name and price are required');
        // Validate variants
        for (let i = 0; i < form.variants.length; i++) {
            const v = form.variants[i];
            if (!v.name.trim()) return toast.error(`Variant ${i + 1}: Name is required`);
            if (v.price === '' || v.price === null || v.price === undefined) return toast.error(`Variant ${i + 1}: Price is required`);
        }
        setLoading(true);
        await onSave({ ...product, ...form });
        setLoading(false);
    };

    const addVariant = () => {
        setForm(f => ({ ...f, variants: [...f.variants, { name: '', price: '', originalPrice: '', buyLink: '' }] }));
    };

    const removeVariant = (idx) => {
        setForm(f => ({ ...f, variants: f.variants.filter((_, i) => i !== idx) }));
    };

    const updateVariant = (idx, field, value) => {
        setForm(f => ({
            ...f,
            variants: f.variants.map((v, i) => i === idx ? { ...v, [field]: value } : v)
        }));
    };

    const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: 8, border: `1.5px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' };
    const labelStyle = { display: 'block', fontSize: 11, fontWeight: 700, color: T.textMute, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 };
    const smallInputStyle = { padding: '8px 10px', borderRadius: 7, border: `1.5px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 13, outline: 'none', boxSizing: 'border-box', width: '100%' };

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={onClose} />
            <div style={{ position: 'relative', width: '100%', maxWidth: 580, background: T.surface, borderRadius: 20, boxShadow: '0 24px 80px rgba(0,0,0,0.3)', overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '92vh' }}>
                <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: T.bg }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: T.text, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 24 }}>🛍️</span> {product ? 'Edit Common Product' : 'Add Common Product'}
                    </div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.textMute, cursor: 'pointer', fontSize: 20 }}><FiX /></button>
                </div>
                <div style={{ padding: '24px', overflowY: 'auto' }}>
                    <form id="common-product-form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <div>
                            <label style={labelStyle}>Product Name *</label>
                            <input style={inputStyle} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
                        </div>
                        <div>
                            <label style={labelStyle}>Description (optional)</label>
                            <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <div>
                                <label style={labelStyle}>Sale Price (₹) *</label>
                                <input style={inputStyle} type="number" min="0" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} required />
                            </div>
                            <div>
                                <label style={labelStyle}>Original Price (₹)</label>
                                <input style={inputStyle} type="number" min="0" value={form.originalPrice} onChange={e => setForm(f => ({ ...f, originalPrice: e.target.value }))} />
                            </div>
                        </div>
                        <div>
                            <label style={labelStyle}>Image URL (optional)</label>
                            <input style={inputStyle} type="url" value={form.imageUrl} onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))} />
                        </div>
                        <div>
                            <label style={labelStyle}>Buy Link / Checkout URL (optional)</label>
                            <input style={inputStyle} type="url" value={form.buyLink} onChange={e => setForm(f => ({ ...f, buyLink: e.target.value }))} />
                        </div>

                        {/* ── Variants Section ── */}
                        <div style={{ border: `1.5px solid ${T.border}`, borderRadius: 12, overflow: 'hidden' }}>
                            <div style={{ padding: '12px 16px', background: T.bg, borderBottom: form.variants.length > 0 ? `1px solid ${T.border}` : 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div>
                                    <div style={{ fontSize: 12, fontWeight: 800, color: T.textMute, textTransform: 'uppercase', letterSpacing: 0.5 }}>🎨 Product Variants <span style={{ fontWeight: 400, textTransform: 'none', fontSize: 11 }}>(optional)</span></div>
                                    <div style={{ fontSize: 11, color: T.textMute, marginTop: 2 }}>e.g. Small, Medium, Large or different sizes/types</div>
                                </div>
                                <button type="button" onClick={addVariant} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(99,102,241,0.3)' }}>
                                    <FiPlus size={13} /> Add Variant
                                </button>
                            </div>

                            {form.variants.length === 0 ? (
                                <div style={{ padding: '20px 16px', textAlign: 'center', color: T.textMute, fontSize: 13 }}>
                                    No variants added yet. Click <strong>Add Variant</strong> to offer multiple options.
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                                    {form.variants.map((v, idx) => (
                                        <div key={idx} style={{ padding: '14px 16px', borderBottom: idx < form.variants.length - 1 ? `1px solid ${T.border}` : 'none', background: idx % 2 === 0 ? T.surface : T.bg }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                                <div style={{ fontSize: 12, fontWeight: 700, color: T.indigo || '#6366f1' }}>Variant {idx + 1}</div>
                                                <button type="button" onClick={() => removeVariant(idx)} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, border: `1px solid #fca5a5`, background: '#fff1f1', color: '#ef4444', fontWeight: 600, fontSize: 11, cursor: 'pointer' }}>
                                                    <FiTrash2 size={11} /> Remove
                                                </button>
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
                                                <div>
                                                    <div style={{ fontSize: 10, fontWeight: 700, color: T.textMute, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Name *</div>
                                                    <input
                                                        style={smallInputStyle}
                                                        placeholder="e.g. Small / 100g"
                                                        value={v.name}
                                                        onChange={e => updateVariant(idx, 'name', e.target.value)}
                                                    />
                                                </div>
                                                <div>
                                                    <div style={{ fontSize: 10, fontWeight: 700, color: T.textMute, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Sale Price (₹) *</div>
                                                    <input
                                                        style={smallInputStyle}
                                                        type="number" min="0"
                                                        placeholder="e.g. 499"
                                                        value={v.price}
                                                        onChange={e => updateVariant(idx, 'price', e.target.value)}
                                                    />
                                                </div>
                                                <div>
                                                    <div style={{ fontSize: 10, fontWeight: 700, color: T.textMute, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Orig. Price (₹)</div>
                                                    <input
                                                        style={smallInputStyle}
                                                        type="number" min="0"
                                                        placeholder="e.g. 799"
                                                        value={v.originalPrice}
                                                        onChange={e => updateVariant(idx, 'originalPrice', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <div style={{ fontSize: 10, fontWeight: 700, color: T.textMute, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Buy Link (optional — overrides main link for this variant)</div>
                                                <input
                                                    style={smallInputStyle}
                                                    type="url"
                                                    placeholder="https://... (leave blank to use main buy link)"
                                                    value={v.buyLink}
                                                    onChange={e => updateVariant(idx, 'buyLink', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4, padding: '12px 16px', background: T.bg, borderRadius: 8, border: `1px solid ${T.border}` }}>
                            <input type="checkbox" id="common-cp-active" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} style={{ width: 18, height: 18, accentColor: T.emerald }} />
                            <label htmlFor="common-cp-active" style={{ fontSize: 14, fontWeight: 600, color: T.text, cursor: 'pointer' }}>Active (Show on Dashboard)</label>
                        </div>
                    </form>
                </div>
                <div style={{ padding: '16px 24px', borderTop: `1px solid ${T.border}`, background: T.bg, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                    <button type="button" onClick={onClose} style={{ padding: '10px 20px', borderRadius: 10, border: `1.5px solid ${T.border}`, background: 'transparent', color: T.textSub, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>Cancel</button>
                    <button type="submit" form="common-product-form" disabled={loading} style={{ padding: '10px 24px', borderRadius: 10, border: 'none', background: loading ? T.emeraldLt : T.emerald, color: '#fff', fontWeight: 700, fontSize: 14, cursor: loading ? 'not-allowed' : 'pointer' }}>
                        {loading ? 'Saving...' : '💾 Save Product'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Bracelet Modal ─────────────────────────────────────────────────────────────
function BraceletModal({ product, initialNumber, onClose, onSave, T }) {
    const isEdit = !!product?._id;
    const [form, setForm] = useState({
        number: product?.number ?? initialNumber ?? 1,
        name: product?.name || '',
        description: product?.description || '',
        price: product?.price ?? '',
        originalPrice: product?.originalPrice ?? '',
        price11Steps: product?.price11Steps ?? '',
        originalPrice11Steps: product?.originalPrice11Steps ?? '',
        price21Steps: product?.price21Steps ?? '',
        originalPrice21Steps: product?.originalPrice21Steps ?? '',
        imageUrl: product?.imageUrl || '',
        buyLink: product?.buyLink || '',
        isActive: product?.isActive !== undefined ? product.isActive : true,
    });
    const [saving, setSaving] = useState(false);

    const handleSubmit = async () => {
        if (!form.name || form.price === '') { toast.error('Name and price are required.'); return; }
        setSaving(true);
        try { await onSave(form); } finally { setSaving(false); }
    };

    const inputStyle = {
        width: '100%', padding: '10px 14px',
        background: T.bg, border: `1.5px solid ${T.border}`,
        borderRadius: 10, color: T.text, fontSize: 14,
        outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
    };
    const labelStyle = { fontSize: 12, fontWeight: 700, color: T.textMute, textTransform: 'uppercase', letterSpacing: 0.7, display: 'block', marginBottom: 6 };

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }} onClick={e => e.target === e.currentTarget && onClose()}>
            <div style={{
                background: T.surface, borderRadius: 20, padding: 32,
                width: '100%', maxWidth: 500,
                border: `1px solid ${T.border}`,
                boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
                animation: 'fadeUp 0.25s ease',
                maxHeight: '90vh', overflowY: 'auto',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                    <h3 style={{ fontSize: 18, fontWeight: 800, color: T.text }}>
                        {isEdit ? '✏️ Edit Crystal Product' : `💎 Add Crystal for Number ${form.number}`}
                    </h3>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: T.textMute, cursor: 'pointer', fontSize: 20 }}>
                        <FiX />
                    </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Number selector (only for new) */}
                    {!isEdit && (
                        <div>
                            <label style={labelStyle}>Number (1–9)</label>
                            <select style={{ ...inputStyle, cursor: 'pointer' }}
                                value={form.number}
                                onChange={e => setForm(f => ({ ...f, number: Number(e.target.value) }))}
                            >
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => <option key={n} value={n}>Number {n}</option>)}
                            </select>
                        </div>
                    )}

                    {/* Name */}
                    <div>
                        <label style={labelStyle}>Product Name</label>
                        <input style={inputStyle} placeholder="e.g. Sun Energy Bracelet"
                            value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                    </div>

                    {/* Description */}
                    <div>
                        <label style={labelStyle}>Short Description</label>
                        <input style={inputStyle} placeholder="e.g. Enhances leadership & confidence"
                            value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                    </div>

                    {/* Price & Original Price (Base / 3 Steps) */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                            <label style={labelStyle}>Base Price (3 Steps) (₹)</label>
                            <input style={inputStyle} type="number" min="0" placeholder="e.g. 799"
                                value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} />
                        </div>
                        <div>
                            <label style={labelStyle}>Original Base Price (₹)</label>
                            <input style={inputStyle} type="number" min="0" placeholder="e.g. 1299"
                                value={form.originalPrice} onChange={e => setForm(f => ({ ...f, originalPrice: e.target.value }))} />
                        </div>
                    </div>

                    {/* 11 Steps Variant */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: '12px', background: 'rgba(160,126,74,0.05)', borderRadius: 10, border: '1px dashed rgba(160,126,74,0.3)' }}>
                        <div>
                            <label style={labelStyle}>11 Steps Price (₹)</label>
                            <input style={inputStyle} type="number" min="0" placeholder="Leave empty for base price"
                                value={form.price11Steps} onChange={e => setForm(f => ({ ...f, price11Steps: e.target.value }))} />
                        </div>
                        <div>
                            <label style={labelStyle}>11 Steps Original (₹)</label>
                            <input style={inputStyle} type="number" min="0"
                                value={form.originalPrice11Steps} onChange={e => setForm(f => ({ ...f, originalPrice11Steps: e.target.value }))} />
                        </div>
                    </div>

                    {/* 21 Steps Variant */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: '12px', background: 'rgba(160,126,74,0.05)', borderRadius: 10, border: '1px dashed rgba(160,126,74,0.3)' }}>
                        <div>
                            <label style={labelStyle}>21 Steps Price (₹)</label>
                            <input style={inputStyle} type="number" min="0" placeholder="Leave empty for base price"
                                value={form.price21Steps} onChange={e => setForm(f => ({ ...f, price21Steps: e.target.value }))} />
                        </div>
                        <div>
                            <label style={labelStyle}>21 Steps Original (₹)</label>
                            <input style={inputStyle} type="number" min="0"
                                value={form.originalPrice21Steps} onChange={e => setForm(f => ({ ...f, originalPrice21Steps: e.target.value }))} />
                        </div>
                    </div>

                    {/* Image URL */}
                    <div>
                        <label style={labelStyle}>Image URL</label>
                        <input style={inputStyle} placeholder="https://... (product image)"
                            value={form.imageUrl} onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))} />
                        {form.imageUrl && (
                            <img src={form.imageUrl} alt="preview"
                                style={{ marginTop: 8, width: '100%', height: 100, objectFit: 'cover', borderRadius: 8, border: `1px solid ${T.border}` }}
                                onError={e => { e.target.style.display = 'none'; }}
                            />
                        )}
                    </div>

                    {/* Buy Link */}
                    <div>
                        <label style={labelStyle}>Buy Link (URL)</label>
                        <input style={inputStyle} placeholder="https://wa.me/... or Razorpay link"
                            value={form.buyLink} onChange={e => setForm(f => ({ ...f, buyLink: e.target.value }))} />
                    </div>

                    {/* Active toggle */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <button
                            onClick={() => setForm(f => ({ ...f, isActive: !f.isActive }))}
                            style={{ width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', background: form.isActive ? '#10b981' : T.border, position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                            <div style={{ position: 'absolute', top: 3, left: form.isActive ? 22 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} />
                        </button>
                        <span style={{ fontSize: 14, color: T.textSub, fontWeight: 600 }}>
                            {form.isActive ? '✅ Active (shown to users)' : '⛔ Inactive (hidden)'}
                        </span>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                        <button onClick={onClose} style={{ flex: 1, padding: '12px', borderRadius: 10, border: `1.5px solid ${T.border}`, background: 'transparent', color: T.textSub, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>Cancel</button>
                        <button onClick={handleSubmit} disabled={saving} style={{ flex: 2, padding: '12px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #a07e4a, #cead80)', color: '#1a0a00', fontWeight: 700, fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 4px 16px rgba(206,173,128,0.3)' }}>
                            <FiSave size={15} /> {saving ? 'Saving…' : (isEdit ? 'Update Product' : 'Save Product')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Order Detail Modal ─────────────────────────────────────────────────────────
function OrderDetailModal({ order, onClose, onUpdateStatus, T }) {
    const [awb, setAwb] = useState(order?.awb || '');
    const [carrierId, setCarrierId] = useState(order?.carrierId || '');
    const [pushing, setPushing] = useState(false);

    if (!order) return null;

    const STATUS_PILLS = {
        pending: { label: '⏳ Pending', bg: '#fef3c7', color: '#d97706', border: '#fcd34d' },
        paid: { label: '💳 Paid', bg: '#e0f2fe', color: '#0369a1', border: '#bae6fd' },
        shipped: { label: '🚚 Shipped', bg: '#ffedd5', color: '#ea580c', border: '#fed7aa' },
        delivered: { label: '✅ Delivered', bg: '#dcfce7', color: '#15803d', border: '#bbf7d0' },
        cancelled: { label: '❌ Cancelled', bg: '#fee2e2', color: '#b91c1c', border: '#fca5a5' }
    };

    const pill = STATUS_PILLS[order.status] || STATUS_PILLS.pending;

    const labelStyle = { fontSize: 11, fontWeight: 700, color: T.textMute, textTransform: 'uppercase', letterSpacing: 0.7, display: 'block', marginBottom: 4 };

    // Format Indian Rupee currency
    const formatRupee = (amount) => {
        return `₹${amount.toLocaleString('en-IN')}`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }} onClick={e => e.target === e.currentTarget && onClose()}>
            <div style={{
                background: T.surface, borderRadius: 20,
                width: '100%', maxWidth: 720, maxHeight: '90vh',
                border: `1px solid ${T.border}`,
                boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
                animation: 'fadeUp 0.25s ease',
                display: 'flex', flexDirection: 'column',
                overflow: 'hidden'
            }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '24px 32px', borderBottom: `1px solid ${T.border}`, background: T.bg }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <h3 style={{ fontSize: 18, fontWeight: 800, color: T.text, margin: 0, fontFamily: 'monospace' }}>
                                ORDER #{order._id.toUpperCase()}
                            </h3>
                            <span style={{
                                background: pill.bg, color: pill.color, border: `1px solid ${pill.border}`,
                                fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 20, textTransform: 'uppercase'
                            }}>{pill.label}</span>
                        </div>
                        <p style={{ fontSize: 12, color: T.textMute, margin: '4px 0 0' }}>
                            Placed on {formatDate(order.createdAt)}
                        </p>
                    </div>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: T.textMute, cursor: 'pointer', fontSize: 20, padding: 4, display: 'flex', alignItems: 'center' }}>
                        <FiX />
                    </button>
                </div>

                {/* Content */}
                <div style={{ padding: 32, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 24 }}>

                    {/* User and Shipping Section */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>

                        {/* Customer & Payment Info */}
                        <div style={{ background: T.bg, padding: 18, borderRadius: 14, border: `1px solid ${T.border}` }}>
                            <h4 style={{ fontSize: 12, fontWeight: 800, color: T.text, margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                                👤 Customer & Payment
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13, color: T.textSub }}>
                                <div>
                                    <span style={labelStyle}>User Account</span>
                                    <span style={{ fontWeight: 600, color: T.text }}>
                                        {order.user ? `${order.user.firstName} ${order.user.lastName}` : 'Guest User'}
                                    </span>
                                    {order.user?.email && <div style={{ fontSize: 12, color: T.textMute }}>{order.user.email}</div>}
                                </div>

                                {order.razorpayOrderId && (
                                    <div style={{ borderTop: `1px dashed ${T.border}`, paddingTop: 10 }}>
                                        <span style={labelStyle}>Razorpay Details</span>
                                        <div style={{ fontFamily: 'monospace', fontSize: 12 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <span style={{ color: T.textMute }}>Order ID:</span>
                                                <span style={{ color: T.text, fontWeight: 600 }}>{order.razorpayOrderId}</span>
                                            </div>
                                            {order.razorpayPaymentId && (
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                                                    <span style={{ color: T.textMute }}>Payment ID:</span>
                                                    <span style={{ color: T.text, fontWeight: 600 }}>{order.razorpayPaymentId}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Shipping Address */}
                        <div style={{ background: T.bg, padding: 18, borderRadius: 14, border: `1px solid ${T.border}` }}>
                            <h4 style={{ fontSize: 12, fontWeight: 800, color: T.text, margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                                🚚 Shipping Address
                            </h4>
                            {order.shippingAddress ? (
                                <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.5 }}>
                                    <span style={labelStyle}>Recipient Name</span>
                                    <div style={{ fontWeight: 700, color: T.text, marginBottom: 8 }}>{order.shippingAddress.fullName}</div>

                                    <span style={labelStyle}>Address</span>
                                    <div>{order.shippingAddress.addressLine1}</div>
                                    {order.shippingAddress.addressLine2 && <div>{order.shippingAddress.addressLine2}</div>}
                                    <div>{order.shippingAddress.city}, {order.shippingAddress.state} - {order.shippingAddress.postalCode}</div>

                                    <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <span style={{ fontWeight: 700 }}>📞 Phone:</span>
                                        <span style={{ color: T.text, fontWeight: 700 }}>{order.shippingAddress.phone}</span>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ fontSize: 13, color: T.textMute }}>No shipping details available.</div>
                            )}
                        </div>

                    </div>

                    {/* Order Items Table */}
                    <div>
                        <h4 style={{ fontSize: 12, fontWeight: 800, color: T.text, margin: '0 0 10px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                            📦 Items Purchased
                        </h4>
                        <div style={{ borderRadius: 12, overflow: 'hidden', border: `1px solid ${T.border}` }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                <thead>
                                    <tr style={{ background: T.bg }}>
                                        <th style={{ padding: '10px 14px', fontSize: 11, fontWeight: 800, color: T.textMute, textTransform: 'uppercase' }}>Product Name</th>
                                        <th style={{ padding: '10px 14px', fontSize: 11, fontWeight: 800, color: T.textMute, textTransform: 'uppercase', textAlign: 'center' }}>Price</th>
                                        <th style={{ padding: '10px 14px', fontSize: 11, fontWeight: 800, color: T.textMute, textTransform: 'uppercase', textAlign: 'center' }}>Qty</th>
                                        <th style={{ padding: '10px 14px', fontSize: 11, fontWeight: 800, color: T.textMute, textTransform: 'uppercase', textAlign: 'right' }}>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {order.items.map((item, idx) => (
                                        <tr key={idx} style={{ borderBottom: idx < order.items.length - 1 ? `1px solid ${T.border}` : 'none' }}>
                                            <td style={{ padding: '12px 14px', fontSize: 13, color: T.text, fontWeight: 600 }}>
                                                {item.name}
                                                {item.variant && <span style={{ color: T.gold || '#d97706', fontSize: 11, marginLeft: 8, fontWeight: 700 }}>({item.variant})</span>}
                                            </td>
                                            <td style={{ padding: '12px 14px', fontSize: 13, color: T.textSub, textAlign: 'center' }}>{formatRupee(item.price)}</td>
                                            <td style={{ padding: '12px 14px', fontSize: 13, color: T.textSub, textAlign: 'center', fontWeight: 700 }}>{item.quantity}</td>
                                            <td style={{ padding: '12px 14px', fontSize: 13, color: T.text, textAlign: 'right', fontWeight: 700 }}>{formatRupee(item.price * item.quantity)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Order Summary Pricing */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, marginTop: 14, paddingRight: 8 }}>
                            <div style={{ display: 'flex', gap: 40, fontSize: 13, color: T.textSub }}>
                                <span>Items Subtotal:</span>
                                <span style={{ fontWeight: 600, minWidth: 80, textAlign: 'right' }}>{formatRupee(order.totalAmount)}</span>
                            </div>
                            {order.couponCode && (
                                <div style={{ display: 'flex', gap: 40, fontSize: 13, color: '#16a34a', fontWeight: 600 }}>
                                    <span>🎟 Coupon Discount ({order.couponCode}):</span>
                                    <span style={{ minWidth: 80, textAlign: 'right' }}>-{formatRupee(order.discountAmount)}</span>
                                </div>
                            )}
                            <div style={{ display: 'flex', gap: 40, fontSize: 15, fontWeight: 800, color: T.text, borderTop: `1.5px solid ${T.border}`, paddingTop: 8, marginTop: 4 }}>
                                <span>Grand Total:</span>
                                <span style={{ color: T.gold, minWidth: 80, textAlign: 'right' }}>{formatRupee(order.finalAmount)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Timeline & Status Logistics */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                        <div style={{ background: T.bg, padding: 18, borderRadius: 14, border: `1px solid ${T.border}` }}>
                            <h4 style={{ fontSize: 12, fontWeight: 800, color: T.text, margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                                🕒 Order History Timeline
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12.5, color: T.textSub }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: `1px dashed ${T.border}`, paddingBottom: 6 }}>
                                    <span style={{ fontWeight: 600 }}>🛒 Order Created:</span>
                                    <span>{formatDate(order.createdAt)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: `1px dashed ${T.border}`, paddingBottom: 6 }}>
                                    <span style={{ fontWeight: 600 }}>💳 Payment Authenticated:</span>
                                    <span style={{ color: order.paidAt ? T.emerald : T.textMute, fontWeight: order.paidAt ? 600 : 400 }}>
                                        {order.paidAt ? formatDate(order.paidAt) : 'Unpaid / Pending'}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: `1px dashed ${T.border}`, paddingBottom: 6 }}>
                                    <span style={{ fontWeight: 600 }}>🚚 Shipped Status:</span>
                                    <span style={{ color: order.shippedAt ? T.amber : T.textMute, fontWeight: order.shippedAt ? 600 : 400 }}>
                                        {order.shippedAt ? formatDate(order.shippedAt) : 'Not Shipped'}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontWeight: 600 }}>🎉 Delivery Completed:</span>
                                    <span style={{ color: order.deliveredAt ? T.emerald : T.textMute, fontWeight: order.deliveredAt ? 600 : 400 }}>
                                        {order.deliveredAt ? formatDate(order.deliveredAt) : 'Undelivered'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Shipway Integration Block */}
                        <div style={{ background: '#f5f7fa', padding: 18, borderRadius: 14, border: '1px solid #cbd5e1' }}>
                            <h4 style={{ fontSize: 12, fontWeight: 800, color: '#1e293b', margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                                📦 Shipway Logistics Tracking
                            </h4>
                            
                            {order.shipwayTrackingUrl ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13, color: '#475569' }}>
                                    <div>
                                        <span style={labelStyle}>Tracking AWB</span>
                                        <span style={{ fontWeight: 700, color: '#0f172a' }}>{order.awb}</span>
                                    </div>
                                    <div>
                                        <span style={labelStyle}>Carrier ID</span>
                                        <span style={{ fontWeight: 700, color: '#0f172a' }}>{order.carrierId}</span>
                                    </div>
                                    <div>
                                        <span style={labelStyle}>Shipway Status</span>
                                        <span style={{ fontWeight: 800, color: '#3b82f6', background: '#eff6ff', padding: '2px 8px', borderRadius: 12 }}>
                                            {order.shipwayStatus || 'Pushed'}
                                        </span>
                                    </div>
                                    <a href={order.shipwayTrackingUrl} target="_blank" rel="noreferrer" style={{ marginTop: 8, textAlign: 'center', display: 'block', padding: '8px 0', background: '#3b82f6', color: '#fff', fontWeight: 700, borderRadius: 8, textDecoration: 'none' }}>
                                        Track Package on Shipway
                                    </a>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    <p style={{ fontSize: 12, color: '#64748b', margin: 0, lineHeight: 1.4 }}>
                                        Enter AWB and Carrier ID to push this order to Shipway for automated tracking updates.
                                    </p>
                                    <div>
                                        <label style={labelStyle}>Carrier ID</label>
                                        <input type="number" value={carrierId} onChange={e => setCarrierId(e.target.value)} placeholder="e.g. 123" style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, outline: 'none' }} />
                                    </div>
                                    <div>
                                        <label style={labelStyle}>AWB Number</label>
                                        <input type="text" value={awb} onChange={e => setAwb(e.target.value)} placeholder="e.g. TRACK12345678" style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, outline: 'none' }} />
                                    </div>
                                    <button 
                                        onClick={async () => {
                                            if (!awb || !carrierId) return toast.error("AWB and Carrier ID are required");
                                            setPushing(true);
                                            try {
                                                const res = await API.post(`/admin/orders/${order._id}/shipway`, { awb, carrierId });
                                                if(res.data.success) {
                                                    toast.success("Successfully pushed to Shipway!");
                                                    onClose(); // Close modal to refresh list, or we could update local state
                                                }
                                            } catch(e) {
                                                toast.error(e.response?.data?.message || "Failed to push to Shipway");
                                            } finally {
                                                setPushing(false);
                                            }
                                        }}
                                        disabled={pushing}
                                        style={{ width: '100%', padding: '10px 0', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: pushing ? 'not-allowed' : 'pointer', transition: 'all 0.2s', marginTop: 4 }}>
                                        {pushing ? 'Pushing...' : '🚀 Push to Shipway'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Logistics Fulfillment Actions inside Modal */}
                    <div>
                        <h4 style={{ fontSize: 12, fontWeight: 800, color: T.text, margin: '0 0 10px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                            ⚙️ Admin Fulfillment Actions
                        </h4>
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            {order.status === 'pending' && (
                                <button
                                    onClick={() => onUpdateStatus(order._id, 'paid')}
                                    style={{
                                        flex: 1, padding: '12px 18px', borderRadius: 10, border: 'none',
                                        background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff',
                                        fontWeight: 800, fontSize: 13.5, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                                >
                                    💳 Mark as Paid
                                </button>
                            )}

                            {order.status === 'paid' && (
                                <button
                                    onClick={() => onUpdateStatus(order._id, 'shipped')}
                                    style={{
                                        flex: 1, padding: '12px 18px', borderRadius: 10, border: 'none',
                                        background: 'linear-gradient(135deg, #f97316, #ea580c)', color: '#fff',
                                        fontWeight: 800, fontSize: 13.5, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                                >
                                    🚚 Mark as Shipped
                                </button>
                            )}

                            {order.status === 'shipped' && (
                                <button
                                    onClick={() => onUpdateStatus(order._id, 'delivered')}
                                    style={{
                                        flex: 1, padding: '12px 18px', borderRadius: 10, border: 'none',
                                        background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff',
                                        fontWeight: 800, fontSize: 13.5, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                                >
                                    ✅ Mark as Delivered
                                </button>
                            )}

                            {['pending', 'paid', 'shipped'].includes(order.status) && (
                                <button
                                    onClick={() => { if (window.confirm('Cancel and refund this order?')) onUpdateStatus(order._id, 'cancelled'); }}
                                    style={{
                                        padding: '12px 18px', borderRadius: 10, border: `1.5px solid ${T.rose}`,
                                        background: 'transparent', color: T.rose, fontWeight: 700, fontSize: 13,
                                        cursor: 'pointer', transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.background = `${T.rose}10`; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                >
                                    Cancel Order
                                </button>
                            )}

                            {order.status === 'delivered' && (
                                <div style={{
                                    flex: 1, padding: '12px 18px', borderRadius: 10, background: T.emeraldLt,
                                    border: `1.5px solid ${T.emerald}20`, textAlign: 'center', fontSize: 13.5,
                                    color: T.emerald, fontWeight: 800
                                }}>
                                    🎉 This order has been successfully fulfilled & delivered.
                                </div>
                            )}

                            {order.status === 'cancelled' && (
                                <div style={{
                                    flex: 1, padding: '12px 18px', borderRadius: 10, background: T.roseLt,
                                    border: `1.5px solid ${T.rose}20`, textAlign: 'center', fontSize: 13.5,
                                    color: T.rose, fontWeight: 800
                                }}>
                                    ❌ This order was cancelled.
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}
